import React, { useContext, useRef, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { PDFDownloadLink, usePDF } from '@react-pdf/renderer';
import PdfDocPlan from '../components/PdfDocPlan';
import {
    makeStyles,
    Typography,
    Button,
    Grid,
    TableContainer,
    Table,
    TableBody,
    TableRow,
    TableCell,
} from '@material-ui/core';
import { GlobalContext } from '../contexts/GlobalContext';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    Tooltip,
    Legend,
    ResponsiveContainer,
} from 'recharts';
import clsx from 'clsx';
import arrowThin from '../images/arrow-thin.svg';
import DemoModal from '../components/DemoModal';
import postscribe from 'postscribe';
import { useMediaQuery } from 'react-responsive';

const useStyles = makeStyles((theme) => ({
    root: {},
    header: {
        marginBottom: '2rem',
    },
    headerContent: {
        padding: '2rem 0 1.5rem',
        borderBottom: '2px solid #000',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-end',
        [theme.breakpoints.down('sm')]: {
            flexWrap: 'wrap',
        },
    },
    headerContentRight: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-end',
        minWidth: 'fit-content',
    },
    headerTitle: {
        fontWeight: 700,
        marginRight: '4.5rem',
        [theme.breakpoints.down('sm')]: {
            fontSize: '2rem',
            lineHeight: '1.6',
            marginRight: '3rem',
        },
    },
    headerDemo: {
        textAlign: 'right',
        padding: '1rem 0 0',
    },
    detailsContainer: {
        marginBottom: '4rem',
        [theme.breakpoints.down('sm')]: {
            marginBottom: '2rem',
        },
    },
    detailsTitle: {
        marginBottom: '2rem',
        width: 'fit-content',
        '&::after': {
            content: '""',
            display: 'block',
            float: 'right',
            width: '30px',
            height: '35px',
            marginTop: '0.2rem',
            marginLeft: '2rem',
            background: `url(${arrowThin}) no-repeat center`,
            backgroundSize: '100% 100%',
        },
    },
    detailsText: {
        marginBottom: '2rem',
        color: '#6a6a6a',
    },
    detailsRight: {
        [theme.breakpoints.down('sm')]: {
            display: 'flex',
            flexDirection: 'column-reverse',
        },
    },
    detailsDownload: {
        textAlign: 'right',
        marginBottom: '4rem',
        [theme.breakpoints.down('sm')]: {
            margin: '3rem 0',
        },
    },
    tableHasBorderTop: {
        borderTop: '1px solid #000',
    },
    tableHasBorder: {
        borderBottom: '1px solid #000',
    },
    tableRowFirst: {
        color: '#878787',
    },
    tableInputContent: {
        fontSize: '1.4rem',
        fontWeight: 700,
    },
    tableOutputTitle: {
        fontSize: '1.4rem',
        [theme.breakpoints.down('md')]: {
            fontSize: '1.2rem',
        },
        [theme.breakpoints.down('xs')]: {
            fontSize: '1rem',
        },
    },
    tableOutputContent: {
        fontSize: '2.2rem',
        fontWeight: 700,
        [theme.breakpoints.down('md')]: {
            fontSize: '1.8rem',
        },
        [theme.breakpoints.down('xs')]: {
            fontSize: '1.4rem',
        },
    },
    tableOutputContentMed: {
        fontSize: '1.4rem',
        fontWeight: 700,
    },
    tableFeatureTitle: {
        fontSize: '1.4rem',
        [theme.breakpoints.down('md')]: {
            fontSize: '1.2rem',
        },
        [theme.breakpoints.down('md')]: {
            fontSize: '1rem',
        },
    },
    tableFeatureContent: {
        fontSize: '1.4rem',
        fontWeight: 700,
        [theme.breakpoints.down('md')]: {
            fontSize: '1.2rem',
        },
        [theme.breakpoints.down('md')]: {
            fontSize: '1rem',
        },
    },
    tableHasNoBorder: {
        border: 0,
    },
    chartContainer: {
        padding: '2rem 0 6rem',
        [theme.breakpoints.down('md')]: {
            padding: '2rem 0 8rem',
        },
    },
    chartMainTitle: {
        paddingBottom: '15px',
        [theme.breakpoints.down('lg')]: {
            fontSize: '2.6rem',
        },
        [theme.breakpoints.down('md')]: {
            fontSize: '2.2rem',
        },
    },
    chartTitle: {
        marginBottom: '30px',
        [theme.breakpoints.down('lg')]: {
            fontSize: '2.6rem',
        },
        [theme.breakpoints.down('md')]: {
            fontSize: '2.2rem',
            marginBottom: '20px',
        },
    },
    chartWrapper: {
        width: '75%',
        height: '400px',
        position: 'relative',
        margin: '0 auto',
        [theme.breakpoints.down('lg')]: {
            height: '350px',
        },
        [theme.breakpoints.down('md')]: {
            width: '98%',
        },
    },
    chartText: {
        position: 'absolute',
        right: '-20px',
        top: '50%',
        bottom: 0,
        transform: 'translate(100%, -50%)',
        [theme.breakpoints.down('md')]: {
            left: '50%',
            right: 0,
            bottom: 0,
            transform: 'translate(-50%, 135%)',
            width: 'fit-content',
        },
    },
    chartContent: {
        maxWidth: '250px',
        lineHeight: 1.3,
    },
    chartFootnote: {
        marginTop: '20px',
        lineHeight: 1.3,
        fontSize: '0.9rem',
        maxWidth: '250px',
    },
    chart: {
        fontFamily: 'Akkurat Pro, Helvetica Neue, Helvetica, Arial, sans-serif',
        '& .recharts-default-legend': {
            marginTop: '60px !important',
            [theme.breakpoints.down('sm')]: {
                marginTop: '30px !important',
            },
        },
        '& .recharts-legend-item': {
            marginRight: '30px !important',
            '&.legend-item-4': {
                marginRight: '0 !important',
            },
        },
        '& .recharts-rectangle.recharts-tooltip-cursor': {
            fill: '#f5f5f5 !important',
        },
    },
    chartHead: {
        flexDirection: 'row',
        justifyContent: 'flex-start',
        alignItems: 'center',
        [theme.breakpoints.down('xs')]: {
            flexDirection: 'column-reverse',
            alignItems: 'flex-start',
        },
    },
    chartHeading: {
        paddingLeft: '180px',
        [theme.breakpoints.down('sm')]: {
            paddingLeft: '2rem',
            fontSize: '2rem',
        },
    },
    chartToggle: {
        paddingLeft: '60px',
        [theme.breakpoints.down('sm')]: {
            paddingLeft: '3rem',
        },
        [theme.breakpoints.down('xs')]: {
            paddingLeft: '2rem',
        },
    },
    chartDesktop: {
        display: 'block',
        [theme.breakpoints.down('sm')]: {
            display: 'none',
        },
    },
    chartMobile: {
        display: 'none',
        [theme.breakpoints.down('sm')]: {
            display: 'block',
            '& .yAxis .recharts-cartesian-axis-ticks': {
                display: 'none !important',
            },
        },
    },
    featuresContainer: {
        marginBottom: '10rem',
        [theme.breakpoints.down('md')]: {
            marginBottom: '6rem',
        },
        [theme.breakpoints.down('xs')]: {
            marginBottom: '3rem',
        },
    },
    featuresTitle: {
        marginBottom: '30px',
    },
    footer: {
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'space-between',
        [theme.breakpoints.down('xs')]: {
            flexDirection: 'column',
            alignItems: 'flex-start',
        },
    },
    footerRight: {
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'space-between',
        [theme.breakpoints.down('xs')]: {
            width: '100%',
        },
    },
    footerImage: {
        [theme.breakpoints.down('sm')]: {
            display: 'none',
        },
    },
    footerEmail: {
        display: 'flex',
    },
    footerCta: {
        marginLeft: '20px',
        [theme.breakpoints.down('xs')]: {
            margin: '3rem 0 0 0',
            width: '100%',
        },
    },
    footerTitle: {
        marginBottom: '10px',
    },
    footerContent: {
        marginBottom: '10px',
    },
    emailInput: {
        '& .MuiInputBase-root': {
            borderRadius: '4px 0 0 4px',
        },
        '& .MuiInputBase-input': {
            padding: '8px 10px',
        },
        [theme.breakpoints.down('xs')]: {
            flexGrow: 1,
        },
    },
    emailButton: {
        borderRadius: '0 4px 4px 0',
        textTransform: 'capitalize',
    },
    editLink: {
        '&::before': {
            content: '""',
            display: 'block',
            float: 'left',
            width: '30px',
            height: '35px',
            background: `url(${arrowThin}) no-repeat center`,
            backgroundSize: '100% 100%',
            transform: 'rotate(180deg)',
            marginRight: '10px',
        },
    },
    downloadButton: {
        textTransform: 'capitalize',
        padding: '0',

        '& a': {
            color: '#fff',
            display: 'block',
            padding: '8px 20px',
        },

        [theme.breakpoints.down('xs')]: {
            width: '100%',
        },
    },
    tableNo: {
        color: '#bbb',
    },
    tableHasBr: {
        textAlign: 'left',
        margin: '0 auto',
        width: 'fit-content',
        '& span': {
            minWidth: '7rem',
            display: 'inline-block',
            [theme.breakpoints.down('md')]: {
                minWidth: '5.5rem',
            },
            [theme.breakpoints.down('xs')]: {
                minWidth: '100%',
            },
        },
    },
    dataTransmission: {
        fontSize: '1rem',
        color: '#bbb',
        display: 'block',
        marginBottom: '0.6rem',
    },
}));

const YourPlan = () => {
    const classes = useStyles();
    const {
        // BELOW IS FROM CHART COMPONENT
        baseResults,
        goLbsCultivationIncrease,
        proLbsCultivationIncrease,
        eliteLbsCultivationIncrease,
        unlimitedLbsCultivationIncrease,
        // BELOW IS FROM OUTPUT COMPONENT
        sqFtCanopy,
        gSqFtHarvest,
        wholesalePrice,
        costToProduce,
        harvestsPerYr,
        propRooms,
        vegRooms,
        flowerRooms,
        dryingRooms,
        goRevenueIncrease,
        proRevenueIncrease,
        eliteRevenueIncrease,
        unlimitedRevenueIncrease,
        goTotalHardwareCost,
        proTotalHardwareCost,
        eliteTotalHardwareCost,
        unlimitedTotalHardwareCost,
        eliteSubscriptionCost,
        unlimitedSubscriptionCost,
        proSoftwareFee,
        goSubstrateCount,
        proSubstrateCount,
        eliteSubstrateCount,
        unlimitedSubstrateCount,
        goClimateCount,
        proClimateCount,
        eliteClimateCount,
        dryingRevenueIncrease,
        unlimitedClimateCount,
        goRepeaterCount,
        proRepeaterCount,
        eliteRepeaterCount,
        unlimitedRepeaterCount,
        goGatewayCount,
        proGatewayCount,
        eliteGatewayCount,
        unlimitedGatewayCount,
    } = useContext(GlobalContext);

    let unlimitedLbsCultivation =
        Math.round(unlimitedLbsCultivationIncrease * 10) / 10;
    let eliteLbsCultivation = Math.round(eliteLbsCultivationIncrease * 10) / 10;
    let proLbsCultivation = Math.round(proLbsCultivationIncrease * 10) / 10;
    let goLbsCultivation = Math.round(goLbsCultivationIncrease * 10) / 10;
    let profits = {
        goYearOne:
            (baseResults.lbsProduced + 0 + 0 / wholesalePrice) *
                wholesalePrice -
            ((baseResults.lbsProduced + 0 + 0 / wholesalePrice) *
                (baseResults.lbsProduced * costToProduce +
                    0 * (costToProduce / 5))) /
                (baseResults.lbsProduced + 0 + 0 / wholesalePrice) -
            baseResults.profit +
            ((baseResults.lbsProduced +
                0 +
                ((baseResults.modelRevenue + 0) * 0.01) / wholesalePrice) *
                wholesalePrice -
                ((baseResults.lbsProduced +
                    0 +
                    ((baseResults.modelRevenue + 0) * 0.01) / wholesalePrice) *
                    (baseResults.lbsProduced * costToProduce +
                        0 * (costToProduce / 5))) /
                    (baseResults.lbsProduced +
                        0 +
                        ((baseResults.modelRevenue + 0) * 0.01) /
                            wholesalePrice) -
                baseResults.profit) +
            ((baseResults.lbsProduced +
                0 +
                ((baseResults.modelRevenue + 0) * 0.01) / wholesalePrice) *
                wholesalePrice -
                ((baseResults.lbsProduced +
                    0 +
                    ((baseResults.modelRevenue + 0) * 0.01) / wholesalePrice) *
                    (baseResults.lbsProduced * costToProduce +
                        0 * (costToProduce / 5))) /
                    (baseResults.lbsProduced +
                        0 +
                        ((baseResults.modelRevenue + 0) * 0.01) /
                            wholesalePrice) -
                baseResults.profit) +
            ((baseResults.lbsProduced +
                0 +
                ((baseResults.modelRevenue + 0) * 0.01) / wholesalePrice) *
                wholesalePrice -
                ((baseResults.lbsProduced +
                    0 +
                    ((baseResults.modelRevenue + 0) * 0.01) / wholesalePrice) *
                    (baseResults.lbsProduced * costToProduce +
                        0 * (costToProduce / 5))) /
                    (baseResults.lbsProduced +
                        0 +
                        ((baseResults.modelRevenue + 0) * 0.01) /
                            wholesalePrice) -
                baseResults.profit) +
            ((baseResults.lbsProduced +
                0 +
                ((baseResults.modelRevenue + 0) * 0.01) / wholesalePrice) *
                wholesalePrice -
                ((baseResults.lbsProduced +
                    0 +
                    ((baseResults.modelRevenue + 0) * 0.01) / wholesalePrice) *
                    (baseResults.lbsProduced * costToProduce +
                        0 * (costToProduce / 5))) /
                    (baseResults.lbsProduced +
                        0 +
                        ((baseResults.modelRevenue + 0) * 0.01) /
                            wholesalePrice) -
                baseResults.profit) +
            ((baseResults.lbsProduced +
                goLbsCultivation +
                ((baseResults.modelRevenue + goRevenueIncrease) * 0.01) /
                    wholesalePrice) *
                wholesalePrice -
                ((baseResults.lbsProduced +
                    goLbsCultivation +
                    ((baseResults.modelRevenue + goRevenueIncrease) * 0.01) /
                        wholesalePrice) *
                    (baseResults.lbsProduced * costToProduce +
                        goLbsCultivation * (costToProduce / 5))) /
                    (baseResults.lbsProduced +
                        goLbsCultivation +
                        ((baseResults.modelRevenue + goRevenueIncrease) *
                            0.01) /
                            wholesalePrice) -
                baseResults.profit) +
            ((baseResults.lbsProduced +
                goLbsCultivation * 2 +
                ((baseResults.modelRevenue + goRevenueIncrease * 2) * 0.01) /
                    wholesalePrice) *
                wholesalePrice -
                ((baseResults.lbsProduced +
                    goLbsCultivation * 2 +
                    ((baseResults.modelRevenue + goRevenueIncrease * 2) *
                        0.01) /
                        wholesalePrice) *
                    (baseResults.lbsProduced * costToProduce +
                        goLbsCultivation * 2 * (costToProduce / 5))) /
                    (baseResults.lbsProduced +
                        goLbsCultivation * 2 +
                        ((baseResults.modelRevenue + goRevenueIncrease * 2) *
                            0.01) /
                            wholesalePrice) -
                baseResults.profit) +
            ((baseResults.lbsProduced +
                goLbsCultivation * 3 +
                ((baseResults.modelRevenue + goRevenueIncrease * 3) * 0.01) /
                    wholesalePrice) *
                wholesalePrice -
                ((baseResults.lbsProduced +
                    goLbsCultivation * 3 +
                    ((baseResults.modelRevenue + goRevenueIncrease * 3) *
                        0.01) /
                        wholesalePrice) *
                    (baseResults.lbsProduced * costToProduce +
                        goLbsCultivation * 3 * (costToProduce / 5))) /
                    (baseResults.lbsProduced +
                        goLbsCultivation * 3 +
                        ((baseResults.modelRevenue + goRevenueIncrease * 3) *
                            0.01) /
                            wholesalePrice) -
                baseResults.profit) +
            ((baseResults.lbsProduced +
                goLbsCultivation * 4 +
                ((baseResults.modelRevenue + goRevenueIncrease * 4) * 0.01) /
                    wholesalePrice) *
                wholesalePrice -
                ((baseResults.lbsProduced +
                    goLbsCultivation * 4 +
                    ((baseResults.modelRevenue + goRevenueIncrease * 4) *
                        0.01) /
                        wholesalePrice) *
                    (baseResults.lbsProduced * costToProduce +
                        goLbsCultivation * 4 * (costToProduce / 5))) /
                    (baseResults.lbsProduced +
                        goLbsCultivation * 4 +
                        ((baseResults.modelRevenue + goRevenueIncrease * 4) *
                            0.01) /
                            wholesalePrice) -
                baseResults.profit) +
            ((baseResults.lbsProduced +
                goLbsCultivation * 5 +
                ((baseResults.modelRevenue + goRevenueIncrease * 5) * 0.01) /
                    wholesalePrice) *
                wholesalePrice -
                ((baseResults.lbsProduced +
                    goLbsCultivation * 5 +
                    ((baseResults.modelRevenue + goRevenueIncrease * 5) *
                        0.01) /
                        wholesalePrice) *
                    (baseResults.lbsProduced * costToProduce +
                        goLbsCultivation * 5 * (costToProduce / 5))) /
                    (baseResults.lbsProduced +
                        goLbsCultivation * 5 +
                        ((baseResults.modelRevenue + goRevenueIncrease * 5) *
                            0.01) /
                            wholesalePrice) -
                baseResults.profit) +
            ((baseResults.lbsProduced +
                goLbsCultivation * 6 +
                ((baseResults.modelRevenue + goRevenueIncrease * 6) * 0.01) /
                    wholesalePrice) *
                wholesalePrice -
                ((baseResults.lbsProduced +
                    goLbsCultivation * 6 +
                    ((baseResults.modelRevenue + goRevenueIncrease * 6) *
                        0.01) /
                        wholesalePrice) *
                    (baseResults.lbsProduced * costToProduce +
                        goLbsCultivation * 6 * (costToProduce / 5))) /
                    (baseResults.lbsProduced +
                        goLbsCultivation * 6 +
                        ((baseResults.modelRevenue + goRevenueIncrease * 6) *
                            0.01) /
                            wholesalePrice) -
                baseResults.profit) +
            ((baseResults.lbsProduced +
                goLbsCultivation * 7 +
                ((baseResults.modelRevenue + goRevenueIncrease * 7) * 0.01) /
                    wholesalePrice) *
                wholesalePrice -
                ((baseResults.lbsProduced +
                    goLbsCultivation * 7 +
                    ((baseResults.modelRevenue + goRevenueIncrease * 7) *
                        0.01) /
                        wholesalePrice) *
                    (baseResults.lbsProduced * costToProduce +
                        goLbsCultivation * 7 * (costToProduce / 5))) /
                    (baseResults.lbsProduced +
                        goLbsCultivation * 7 +
                        ((baseResults.modelRevenue + goRevenueIncrease * 7) *
                            0.01) /
                            wholesalePrice) -
                baseResults.profit),
        goYearTwo:
            (baseResults.lbsProduced +
                goLbsCultivation * 8 +
                ((baseResults.modelRevenue + goRevenueIncrease * 8) * 0.01) /
                    wholesalePrice) *
                wholesalePrice -
            ((baseResults.lbsProduced +
                goLbsCultivation * 8 +
                ((baseResults.modelRevenue + goRevenueIncrease * 8) * 0.01) /
                    wholesalePrice) *
                (baseResults.lbsProduced * costToProduce +
                    goLbsCultivation * 8 * (costToProduce / 5))) /
                (baseResults.lbsProduced +
                    goLbsCultivation * 8 +
                    ((baseResults.modelRevenue + goRevenueIncrease * 8) *
                        0.01) /
                        wholesalePrice) -
            baseResults.profit +
            ((baseResults.lbsProduced +
                goLbsCultivation * 9 +
                ((baseResults.modelRevenue + goRevenueIncrease * 9) * 0.01) /
                    wholesalePrice) *
                wholesalePrice -
                ((baseResults.lbsProduced +
                    goLbsCultivation * 9 +
                    ((baseResults.modelRevenue + goRevenueIncrease * 9) *
                        0.01) /
                        wholesalePrice) *
                    (baseResults.lbsProduced * costToProduce +
                        goLbsCultivation * 9 * (costToProduce / 5))) /
                    (baseResults.lbsProduced +
                        goLbsCultivation * 9 +
                        ((baseResults.modelRevenue + goRevenueIncrease * 9) *
                            0.01) /
                            wholesalePrice) -
                baseResults.profit) +
            ((baseResults.lbsProduced +
                goLbsCultivation * 10 +
                ((baseResults.modelRevenue + goRevenueIncrease * 10) * 0.01) /
                    wholesalePrice) *
                wholesalePrice -
                ((baseResults.lbsProduced +
                    goLbsCultivation * 10 +
                    ((baseResults.modelRevenue + goRevenueIncrease * 10) *
                        0.01) /
                        wholesalePrice) *
                    (baseResults.lbsProduced * costToProduce +
                        goLbsCultivation * 10 * (costToProduce / 5))) /
                    (baseResults.lbsProduced +
                        goLbsCultivation * 10 +
                        ((baseResults.modelRevenue + goRevenueIncrease * 10) *
                            0.01) /
                            wholesalePrice) -
                baseResults.profit) +
            ((baseResults.lbsProduced +
                goLbsCultivation * 11 +
                ((baseResults.modelRevenue + goRevenueIncrease * 11) * 0.01) /
                    wholesalePrice) *
                wholesalePrice -
                ((baseResults.lbsProduced +
                    goLbsCultivation * 11 +
                    ((baseResults.modelRevenue + goRevenueIncrease * 11) *
                        0.01) /
                        wholesalePrice) *
                    (baseResults.lbsProduced * costToProduce +
                        goLbsCultivation * 11 * (costToProduce / 5))) /
                    (baseResults.lbsProduced +
                        goLbsCultivation * 11 +
                        ((baseResults.modelRevenue + goRevenueIncrease * 11) *
                            0.01) /
                            wholesalePrice) -
                baseResults.profit) +
            ((baseResults.lbsProduced +
                goLbsCultivation * 12 +
                ((baseResults.modelRevenue + goRevenueIncrease * 12) * 0.01) /
                    wholesalePrice) *
                wholesalePrice -
                ((baseResults.lbsProduced +
                    goLbsCultivation * 12 +
                    ((baseResults.modelRevenue + goRevenueIncrease * 12) *
                        0.01) /
                        wholesalePrice) *
                    (baseResults.lbsProduced * costToProduce +
                        goLbsCultivation * 12 * (costToProduce / 5))) /
                    (baseResults.lbsProduced +
                        goLbsCultivation * 12 +
                        ((baseResults.modelRevenue + goRevenueIncrease * 12) *
                            0.01) /
                            wholesalePrice) -
                baseResults.profit) +
            ((baseResults.lbsProduced +
                goLbsCultivation * 12 +
                ((baseResults.modelRevenue + goRevenueIncrease * 12) * 0.01) /
                    wholesalePrice) *
                wholesalePrice -
                ((baseResults.lbsProduced +
                    goLbsCultivation * 12 +
                    ((baseResults.modelRevenue + goRevenueIncrease * 12) *
                        0.01) /
                        wholesalePrice) *
                    (baseResults.lbsProduced * costToProduce +
                        goLbsCultivation * 12 * (costToProduce / 5))) /
                    (baseResults.lbsProduced +
                        goLbsCultivation * 12 +
                        ((baseResults.modelRevenue + goRevenueIncrease * 12) *
                            0.01) /
                            wholesalePrice) -
                baseResults.profit) +
            ((baseResults.lbsProduced +
                goLbsCultivation * 12 +
                ((baseResults.modelRevenue + goRevenueIncrease * 12) * 0.01) /
                    wholesalePrice) *
                wholesalePrice -
                ((baseResults.lbsProduced +
                    goLbsCultivation * 12 +
                    ((baseResults.modelRevenue + goRevenueIncrease * 12) *
                        0.01) /
                        wholesalePrice) *
                    (baseResults.lbsProduced * costToProduce +
                        goLbsCultivation * 12 * (costToProduce / 5))) /
                    (baseResults.lbsProduced +
                        goLbsCultivation * 12 +
                        ((baseResults.modelRevenue + goRevenueIncrease * 12) *
                            0.01) /
                            wholesalePrice) -
                baseResults.profit) +
            ((baseResults.lbsProduced +
                goLbsCultivation * 12 +
                ((baseResults.modelRevenue + goRevenueIncrease * 12) * 0.01) /
                    wholesalePrice) *
                wholesalePrice -
                ((baseResults.lbsProduced +
                    goLbsCultivation * 12 +
                    ((baseResults.modelRevenue + goRevenueIncrease * 12) *
                        0.01) /
                        wholesalePrice) *
                    (baseResults.lbsProduced * costToProduce +
                        goLbsCultivation * 12 * (costToProduce / 5))) /
                    (baseResults.lbsProduced +
                        goLbsCultivation * 12 +
                        ((baseResults.modelRevenue + goRevenueIncrease * 12) *
                            0.01) /
                            wholesalePrice) -
                baseResults.profit) +
            ((baseResults.lbsProduced +
                goLbsCultivation * 12 +
                ((baseResults.modelRevenue + goRevenueIncrease * 12) * 0.01) /
                    wholesalePrice) *
                wholesalePrice -
                ((baseResults.lbsProduced +
                    goLbsCultivation * 12 +
                    ((baseResults.modelRevenue + goRevenueIncrease * 12) *
                        0.01) /
                        wholesalePrice) *
                    (baseResults.lbsProduced * costToProduce +
                        goLbsCultivation * 12 * (costToProduce / 5))) /
                    (baseResults.lbsProduced +
                        goLbsCultivation * 12 +
                        ((baseResults.modelRevenue + goRevenueIncrease * 12) *
                            0.01) /
                            wholesalePrice) -
                baseResults.profit) +
            ((baseResults.lbsProduced +
                goLbsCultivation * 12 +
                ((baseResults.modelRevenue + goRevenueIncrease * 12) * 0.01) /
                    wholesalePrice) *
                wholesalePrice -
                ((baseResults.lbsProduced +
                    goLbsCultivation * 12 +
                    ((baseResults.modelRevenue + goRevenueIncrease * 12) *
                        0.01) /
                        wholesalePrice) *
                    (baseResults.lbsProduced * costToProduce +
                        goLbsCultivation * 12 * (costToProduce / 5))) /
                    (baseResults.lbsProduced +
                        goLbsCultivation * 12 +
                        ((baseResults.modelRevenue + goRevenueIncrease * 12) *
                            0.01) /
                            wholesalePrice) -
                baseResults.profit) +
            ((baseResults.lbsProduced +
                goLbsCultivation * 12 +
                ((baseResults.modelRevenue + goRevenueIncrease * 12) * 0.01) /
                    wholesalePrice) *
                wholesalePrice -
                ((baseResults.lbsProduced +
                    goLbsCultivation * 12 +
                    ((baseResults.modelRevenue + goRevenueIncrease * 12) *
                        0.01) /
                        wholesalePrice) *
                    (baseResults.lbsProduced * costToProduce +
                        goLbsCultivation * 12 * (costToProduce / 5))) /
                    (baseResults.lbsProduced +
                        goLbsCultivation * 12 +
                        ((baseResults.modelRevenue + goRevenueIncrease * 12) *
                            0.01) /
                            wholesalePrice) -
                baseResults.profit) +
            ((baseResults.lbsProduced +
                goLbsCultivation * 12 +
                ((baseResults.modelRevenue + goRevenueIncrease * 12) * 0.01) /
                    wholesalePrice) *
                wholesalePrice -
                ((baseResults.lbsProduced +
                    goLbsCultivation * 12 +
                    ((baseResults.modelRevenue + goRevenueIncrease * 12) *
                        0.01) /
                        wholesalePrice) *
                    (baseResults.lbsProduced * costToProduce +
                        goLbsCultivation * 12 * (costToProduce / 5))) /
                    (baseResults.lbsProduced +
                        goLbsCultivation * 12 +
                        ((baseResults.modelRevenue + goRevenueIncrease * 12) *
                            0.01) /
                            wholesalePrice) -
                baseResults.profit),
        goYearThree:
            (baseResults.lbsProduced +
                goLbsCultivation * 12 +
                ((baseResults.modelRevenue + goRevenueIncrease * 12) * 0.01) /
                    wholesalePrice) *
                wholesalePrice -
            ((baseResults.lbsProduced +
                goLbsCultivation * 12 +
                ((baseResults.modelRevenue + goRevenueIncrease * 12) * 0.01) /
                    wholesalePrice) *
                (baseResults.lbsProduced * costToProduce +
                    goLbsCultivation * 12 * (costToProduce / 5))) /
                (baseResults.lbsProduced +
                    goLbsCultivation * 12 +
                    ((baseResults.modelRevenue + goRevenueIncrease * 12) *
                        0.01) /
                        wholesalePrice) -
            baseResults.profit +
            ((baseResults.lbsProduced +
                goLbsCultivation * 12 +
                ((baseResults.modelRevenue + goRevenueIncrease * 12) * 0.01) /
                    wholesalePrice) *
                wholesalePrice -
                ((baseResults.lbsProduced +
                    goLbsCultivation * 12 +
                    ((baseResults.modelRevenue + goRevenueIncrease * 12) *
                        0.01) /
                        wholesalePrice) *
                    (baseResults.lbsProduced * costToProduce +
                        goLbsCultivation * 12 * (costToProduce / 5))) /
                    (baseResults.lbsProduced +
                        goLbsCultivation * 12 +
                        ((baseResults.modelRevenue + goRevenueIncrease * 12) *
                            0.01) /
                            wholesalePrice) -
                baseResults.profit) +
            ((baseResults.lbsProduced +
                goLbsCultivation * 12 +
                ((baseResults.modelRevenue + goRevenueIncrease * 12) * 0.01) /
                    wholesalePrice) *
                wholesalePrice -
                ((baseResults.lbsProduced +
                    goLbsCultivation * 12 +
                    ((baseResults.modelRevenue + goRevenueIncrease * 12) *
                        0.01) /
                        wholesalePrice) *
                    (baseResults.lbsProduced * costToProduce +
                        goLbsCultivation * 12 * (costToProduce / 5))) /
                    (baseResults.lbsProduced +
                        goLbsCultivation * 12 +
                        ((baseResults.modelRevenue + goRevenueIncrease * 12) *
                            0.01) /
                            wholesalePrice) -
                baseResults.profit) +
            ((baseResults.lbsProduced +
                goLbsCultivation * 12 +
                ((baseResults.modelRevenue + goRevenueIncrease * 12) * 0.01) /
                    wholesalePrice) *
                wholesalePrice -
                ((baseResults.lbsProduced +
                    goLbsCultivation * 12 +
                    ((baseResults.modelRevenue + goRevenueIncrease * 12) *
                        0.01) /
                        wholesalePrice) *
                    (baseResults.lbsProduced * costToProduce +
                        goLbsCultivation * 12 * (costToProduce / 5))) /
                    (baseResults.lbsProduced +
                        goLbsCultivation * 12 +
                        ((baseResults.modelRevenue + goRevenueIncrease * 12) *
                            0.01) /
                            wholesalePrice) -
                baseResults.profit) +
            ((baseResults.lbsProduced +
                goLbsCultivation * 12 +
                ((baseResults.modelRevenue + goRevenueIncrease * 12) * 0.01) /
                    wholesalePrice) *
                wholesalePrice -
                ((baseResults.lbsProduced +
                    goLbsCultivation * 12 +
                    ((baseResults.modelRevenue + goRevenueIncrease * 12) *
                        0.01) /
                        wholesalePrice) *
                    (baseResults.lbsProduced * costToProduce +
                        goLbsCultivation * 12 * (costToProduce / 5))) /
                    (baseResults.lbsProduced +
                        goLbsCultivation * 12 +
                        ((baseResults.modelRevenue + goRevenueIncrease * 12) *
                            0.01) /
                            wholesalePrice) -
                baseResults.profit) +
            ((baseResults.lbsProduced +
                goLbsCultivation * 12 +
                ((baseResults.modelRevenue + goRevenueIncrease * 12) * 0.01) /
                    wholesalePrice) *
                wholesalePrice -
                ((baseResults.lbsProduced +
                    goLbsCultivation * 12 +
                    ((baseResults.modelRevenue + goRevenueIncrease * 12) *
                        0.01) /
                        wholesalePrice) *
                    (baseResults.lbsProduced * costToProduce +
                        goLbsCultivation * 12 * (costToProduce / 5))) /
                    (baseResults.lbsProduced +
                        goLbsCultivation * 12 +
                        ((baseResults.modelRevenue + goRevenueIncrease * 12) *
                            0.01) /
                            wholesalePrice) -
                baseResults.profit) +
            ((baseResults.lbsProduced +
                goLbsCultivation * 12 +
                ((baseResults.modelRevenue + goRevenueIncrease * 12) * 0.01) /
                    wholesalePrice) *
                wholesalePrice -
                ((baseResults.lbsProduced +
                    goLbsCultivation * 12 +
                    ((baseResults.modelRevenue + goRevenueIncrease * 12) *
                        0.01) /
                        wholesalePrice) *
                    (baseResults.lbsProduced * costToProduce +
                        goLbsCultivation * 12 * (costToProduce / 5))) /
                    (baseResults.lbsProduced +
                        goLbsCultivation * 12 +
                        ((baseResults.modelRevenue + goRevenueIncrease * 12) *
                            0.01) /
                            wholesalePrice) -
                baseResults.profit) +
            ((baseResults.lbsProduced +
                goLbsCultivation * 12 +
                ((baseResults.modelRevenue + goRevenueIncrease * 12) * 0.01) /
                    wholesalePrice) *
                wholesalePrice -
                ((baseResults.lbsProduced +
                    goLbsCultivation * 12 +
                    ((baseResults.modelRevenue + goRevenueIncrease * 12) *
                        0.01) /
                        wholesalePrice) *
                    (baseResults.lbsProduced * costToProduce +
                        goLbsCultivation * 12 * (costToProduce / 5))) /
                    (baseResults.lbsProduced +
                        goLbsCultivation * 12 +
                        ((baseResults.modelRevenue + goRevenueIncrease * 12) *
                            0.01) /
                            wholesalePrice) -
                baseResults.profit) +
            ((baseResults.lbsProduced +
                goLbsCultivation * 12 +
                ((baseResults.modelRevenue + goRevenueIncrease * 12) * 0.01) /
                    wholesalePrice) *
                wholesalePrice -
                ((baseResults.lbsProduced +
                    goLbsCultivation * 12 +
                    ((baseResults.modelRevenue + goRevenueIncrease * 12) *
                        0.01) /
                        wholesalePrice) *
                    (baseResults.lbsProduced * costToProduce +
                        goLbsCultivation * 12 * (costToProduce / 5))) /
                    (baseResults.lbsProduced +
                        goLbsCultivation * 12 +
                        ((baseResults.modelRevenue + goRevenueIncrease * 12) *
                            0.01) /
                            wholesalePrice) -
                baseResults.profit) +
            ((baseResults.lbsProduced +
                goLbsCultivation * 12 +
                ((baseResults.modelRevenue + goRevenueIncrease * 12) * 0.01) /
                    wholesalePrice) *
                wholesalePrice -
                ((baseResults.lbsProduced +
                    goLbsCultivation * 12 +
                    ((baseResults.modelRevenue + goRevenueIncrease * 12) *
                        0.01) /
                        wholesalePrice) *
                    (baseResults.lbsProduced * costToProduce +
                        goLbsCultivation * 12 * (costToProduce / 5))) /
                    (baseResults.lbsProduced +
                        goLbsCultivation * 12 +
                        ((baseResults.modelRevenue + goRevenueIncrease * 12) *
                            0.01) /
                            wholesalePrice) -
                baseResults.profit) +
            ((baseResults.lbsProduced +
                goLbsCultivation * 12 +
                ((baseResults.modelRevenue + goRevenueIncrease * 12) * 0.01) /
                    wholesalePrice) *
                wholesalePrice -
                ((baseResults.lbsProduced +
                    goLbsCultivation * 12 +
                    ((baseResults.modelRevenue + goRevenueIncrease * 12) *
                        0.01) /
                        wholesalePrice) *
                    (baseResults.lbsProduced * costToProduce +
                        goLbsCultivation * 12 * (costToProduce / 5))) /
                    (baseResults.lbsProduced +
                        goLbsCultivation * 12 +
                        ((baseResults.modelRevenue + goRevenueIncrease * 12) *
                            0.01) /
                            wholesalePrice) -
                baseResults.profit) +
            ((baseResults.lbsProduced +
                goLbsCultivation * 12 +
                ((baseResults.modelRevenue + goRevenueIncrease * 12) * 0.01) /
                    wholesalePrice) *
                wholesalePrice -
                ((baseResults.lbsProduced +
                    goLbsCultivation * 12 +
                    ((baseResults.modelRevenue + goRevenueIncrease * 12) *
                        0.01) /
                        wholesalePrice) *
                    (baseResults.lbsProduced * costToProduce +
                        goLbsCultivation * 12 * (costToProduce / 5))) /
                    (baseResults.lbsProduced +
                        goLbsCultivation * 12 +
                        ((baseResults.modelRevenue + goRevenueIncrease * 12) *
                            0.01) /
                            wholesalePrice) -
                baseResults.profit),
        proYearOne:
            (baseResults.lbsProduced + 0 + 0 / wholesalePrice) *
                wholesalePrice -
            ((baseResults.lbsProduced + 0 + 0 / wholesalePrice) *
                (baseResults.lbsProduced * costToProduce +
                    0 * (costToProduce / 5))) /
                (baseResults.lbsProduced + 0 + 0 / wholesalePrice) -
            baseResults.profit +
            ((baseResults.lbsProduced +
                0 +
                ((baseResults.modelRevenue + 0) * 0.01) / wholesalePrice) *
                wholesalePrice -
                ((baseResults.lbsProduced +
                    0 +
                    ((baseResults.modelRevenue + 0) * 0.01) / wholesalePrice) *
                    (baseResults.lbsProduced * costToProduce +
                        0 * (costToProduce / 5))) /
                    (baseResults.lbsProduced +
                        0 +
                        ((baseResults.modelRevenue + 0) * 0.01) /
                            wholesalePrice) -
                baseResults.profit) +
            ((baseResults.lbsProduced +
                0 +
                ((baseResults.modelRevenue + 0) * 0.01) / wholesalePrice) *
                wholesalePrice -
                ((baseResults.lbsProduced +
                    0 +
                    ((baseResults.modelRevenue + 0) * 0.01) / wholesalePrice) *
                    (baseResults.lbsProduced * costToProduce +
                        0 * (costToProduce / 5))) /
                    (baseResults.lbsProduced +
                        0 +
                        ((baseResults.modelRevenue + 0) * 0.01) /
                            wholesalePrice) -
                baseResults.profit) +
            ((baseResults.lbsProduced +
                0 +
                ((baseResults.modelRevenue + 0) * 0.01) / wholesalePrice) *
                wholesalePrice -
                ((baseResults.lbsProduced +
                    0 +
                    ((baseResults.modelRevenue + 0) * 0.01) / wholesalePrice) *
                    (baseResults.lbsProduced * costToProduce +
                        0 * (costToProduce / 5))) /
                    (baseResults.lbsProduced +
                        0 +
                        ((baseResults.modelRevenue + 0) * 0.01) /
                            wholesalePrice) -
                baseResults.profit) +
            ((baseResults.lbsProduced +
                proLbsCultivation +
                ((baseResults.modelRevenue + proRevenueIncrease) * 0.01) /
                    wholesalePrice) *
                wholesalePrice -
                ((baseResults.lbsProduced +
                    proLbsCultivation +
                    ((baseResults.modelRevenue + proRevenueIncrease) * 0.01) /
                        wholesalePrice) *
                    (baseResults.lbsProduced * costToProduce +
                        proLbsCultivation * (costToProduce / 5))) /
                    (baseResults.lbsProduced +
                        proLbsCultivation +
                        ((baseResults.modelRevenue + proRevenueIncrease) *
                            0.01) /
                            wholesalePrice) -
                baseResults.profit) +
            ((baseResults.lbsProduced +
                proLbsCultivation * 2 +
                ((baseResults.modelRevenue + proRevenueIncrease * 2) * 0.01) /
                    wholesalePrice) *
                wholesalePrice -
                ((baseResults.lbsProduced +
                    proLbsCultivation * 2 +
                    ((baseResults.modelRevenue + proRevenueIncrease * 2) *
                        0.01) /
                        wholesalePrice) *
                    (baseResults.lbsProduced * costToProduce +
                        proLbsCultivation * 2 * (costToProduce / 5))) /
                    (baseResults.lbsProduced +
                        proLbsCultivation * 2 +
                        ((baseResults.modelRevenue + proRevenueIncrease * 2) *
                            0.01) /
                            wholesalePrice) -
                baseResults.profit) +
            ((baseResults.lbsProduced +
                proLbsCultivation * 3 +
                ((baseResults.modelRevenue + proRevenueIncrease * 3) * 0.01) /
                    wholesalePrice) *
                wholesalePrice -
                ((baseResults.lbsProduced +
                    proLbsCultivation * 3 +
                    ((baseResults.modelRevenue + proRevenueIncrease * 3) *
                        0.01) /
                        wholesalePrice) *
                    (baseResults.lbsProduced * costToProduce +
                        proLbsCultivation * 3 * (costToProduce / 5))) /
                    (baseResults.lbsProduced +
                        proLbsCultivation * 3 +
                        ((baseResults.modelRevenue + proRevenueIncrease * 3) *
                            0.01) /
                            wholesalePrice) -
                baseResults.profit) +
            ((baseResults.lbsProduced +
                proLbsCultivation * 4 +
                ((baseResults.modelRevenue + proRevenueIncrease * 4) * 0.01) /
                    wholesalePrice) *
                wholesalePrice -
                ((baseResults.lbsProduced +
                    proLbsCultivation * 4 +
                    ((baseResults.modelRevenue + proRevenueIncrease * 4) *
                        0.01) /
                        wholesalePrice) *
                    (baseResults.lbsProduced * costToProduce +
                        proLbsCultivation * 4 * (costToProduce / 5))) /
                    (baseResults.lbsProduced +
                        proLbsCultivation * 4 +
                        ((baseResults.modelRevenue + proRevenueIncrease * 4) *
                            0.01) /
                            wholesalePrice) -
                baseResults.profit) +
            ((baseResults.lbsProduced +
                proLbsCultivation * 5 +
                ((baseResults.modelRevenue + proRevenueIncrease * 5) * 0.01) /
                    wholesalePrice) *
                wholesalePrice -
                ((baseResults.lbsProduced +
                    proLbsCultivation * 5 +
                    ((baseResults.modelRevenue + proRevenueIncrease * 5) *
                        0.01) /
                        wholesalePrice) *
                    (baseResults.lbsProduced * costToProduce +
                        proLbsCultivation * 5 * (costToProduce / 5))) /
                    (baseResults.lbsProduced +
                        proLbsCultivation * 5 +
                        ((baseResults.modelRevenue + proRevenueIncrease * 5) *
                            0.01) /
                            wholesalePrice) -
                baseResults.profit) +
            ((baseResults.lbsProduced +
                proLbsCultivation * 6 +
                ((baseResults.modelRevenue + proRevenueIncrease * 6) * 0.01) /
                    wholesalePrice) *
                wholesalePrice -
                ((baseResults.lbsProduced +
                    proLbsCultivation * 6 +
                    ((baseResults.modelRevenue + proRevenueIncrease * 6) *
                        0.01) /
                        wholesalePrice) *
                    (baseResults.lbsProduced * costToProduce +
                        proLbsCultivation * 6 * (costToProduce / 5))) /
                    (baseResults.lbsProduced +
                        proLbsCultivation * 6 +
                        ((baseResults.modelRevenue + proRevenueIncrease * 6) *
                            0.01) /
                            wholesalePrice) -
                baseResults.profit) +
            ((baseResults.lbsProduced +
                proLbsCultivation * 7 +
                ((baseResults.modelRevenue + proRevenueIncrease * 7) * 0.01) /
                    wholesalePrice) *
                wholesalePrice -
                ((baseResults.lbsProduced +
                    proLbsCultivation * 7 +
                    ((baseResults.modelRevenue + proRevenueIncrease * 7) *
                        0.01) /
                        wholesalePrice) *
                    (baseResults.lbsProduced * costToProduce +
                        proLbsCultivation * 7 * (costToProduce / 5))) /
                    (baseResults.lbsProduced +
                        proLbsCultivation * 7 +
                        ((baseResults.modelRevenue + proRevenueIncrease * 7) *
                            0.01) /
                            wholesalePrice) -
                baseResults.profit) +
            ((baseResults.lbsProduced +
                proLbsCultivation * 8 +
                ((baseResults.modelRevenue + proRevenueIncrease * 8) * 0.01) /
                    wholesalePrice) *
                wholesalePrice -
                ((baseResults.lbsProduced +
                    proLbsCultivation * 8 +
                    ((baseResults.modelRevenue + proRevenueIncrease * 8) *
                        0.01) /
                        wholesalePrice) *
                    (baseResults.lbsProduced * costToProduce +
                        proLbsCultivation * 8 * (costToProduce / 5))) /
                    (baseResults.lbsProduced +
                        proLbsCultivation * 8 +
                        ((baseResults.modelRevenue + proRevenueIncrease * 8) *
                            0.01) /
                            wholesalePrice) -
                baseResults.profit),
        proYearTwo:
            (baseResults.lbsProduced +
                proLbsCultivation * 9 +
                ((baseResults.modelRevenue + proRevenueIncrease * 9) * 0.01) /
                    wholesalePrice) *
                wholesalePrice -
            ((baseResults.lbsProduced +
                proLbsCultivation * 9 +
                ((baseResults.modelRevenue + proRevenueIncrease * 9) * 0.01) /
                    wholesalePrice) *
                (baseResults.lbsProduced * costToProduce +
                    proLbsCultivation * 9 * (costToProduce / 5))) /
                (baseResults.lbsProduced +
                    proLbsCultivation * 9 +
                    ((baseResults.modelRevenue + proRevenueIncrease * 9) *
                        0.01) /
                        wholesalePrice) -
            baseResults.profit +
            ((baseResults.lbsProduced +
                proLbsCultivation * 10 +
                ((baseResults.modelRevenue + proRevenueIncrease * 10) * 0.01) /
                    wholesalePrice) *
                wholesalePrice -
                ((baseResults.lbsProduced +
                    proLbsCultivation * 10 +
                    ((baseResults.modelRevenue + proRevenueIncrease * 10) *
                        0.01) /
                        wholesalePrice) *
                    (baseResults.lbsProduced * costToProduce +
                        proLbsCultivation * 10 * (costToProduce / 5))) /
                    (baseResults.lbsProduced +
                        proLbsCultivation * 10 +
                        ((baseResults.modelRevenue + proRevenueIncrease * 10) *
                            0.01) /
                            wholesalePrice) -
                baseResults.profit) +
            ((baseResults.lbsProduced +
                proLbsCultivation * 11 +
                ((baseResults.modelRevenue + proRevenueIncrease * 11) * 0.01) /
                    wholesalePrice) *
                wholesalePrice -
                ((baseResults.lbsProduced +
                    proLbsCultivation * 11 +
                    ((baseResults.modelRevenue + proRevenueIncrease * 11) *
                        0.01) /
                        wholesalePrice) *
                    (baseResults.lbsProduced * costToProduce +
                        proLbsCultivation * 11 * (costToProduce / 5))) /
                    (baseResults.lbsProduced +
                        proLbsCultivation * 11 +
                        ((baseResults.modelRevenue + proRevenueIncrease * 11) *
                            0.01) /
                            wholesalePrice) -
                baseResults.profit) +
            ((baseResults.lbsProduced +
                proLbsCultivation * 12 +
                ((baseResults.modelRevenue + proRevenueIncrease * 12) * 0.01) /
                    wholesalePrice) *
                wholesalePrice -
                ((baseResults.lbsProduced +
                    proLbsCultivation * 12 +
                    ((baseResults.modelRevenue + proRevenueIncrease * 12) *
                        0.01) /
                        wholesalePrice) *
                    (baseResults.lbsProduced * costToProduce +
                        proLbsCultivation * 12 * (costToProduce / 5))) /
                    (baseResults.lbsProduced +
                        proLbsCultivation * 12 +
                        ((baseResults.modelRevenue + proRevenueIncrease * 12) *
                            0.01) /
                            wholesalePrice) -
                baseResults.profit) +
            ((baseResults.lbsProduced +
                proLbsCultivation * 12 +
                ((baseResults.modelRevenue + proRevenueIncrease * 12) * 0.01) /
                    wholesalePrice) *
                wholesalePrice -
                ((baseResults.lbsProduced +
                    proLbsCultivation * 12 +
                    ((baseResults.modelRevenue + proRevenueIncrease * 12) *
                        0.01) /
                        wholesalePrice) *
                    (baseResults.lbsProduced * costToProduce +
                        proLbsCultivation * 12 * (costToProduce / 5))) /
                    (baseResults.lbsProduced +
                        proLbsCultivation * 12 +
                        ((baseResults.modelRevenue + proRevenueIncrease * 12) *
                            0.01) /
                            wholesalePrice) -
                baseResults.profit) +
            ((baseResults.lbsProduced +
                proLbsCultivation * 12 +
                ((baseResults.modelRevenue + proRevenueIncrease * 12) * 0.01) /
                    wholesalePrice) *
                wholesalePrice -
                ((baseResults.lbsProduced +
                    proLbsCultivation * 12 +
                    ((baseResults.modelRevenue + proRevenueIncrease * 12) *
                        0.01) /
                        wholesalePrice) *
                    (baseResults.lbsProduced * costToProduce +
                        proLbsCultivation * 12 * (costToProduce / 5))) /
                    (baseResults.lbsProduced +
                        proLbsCultivation * 12 +
                        ((baseResults.modelRevenue + proRevenueIncrease * 12) *
                            0.01) /
                            wholesalePrice) -
                baseResults.profit) +
            ((baseResults.lbsProduced +
                proLbsCultivation * 12 +
                ((baseResults.modelRevenue + proRevenueIncrease * 12) * 0.01) /
                    wholesalePrice) *
                wholesalePrice -
                ((baseResults.lbsProduced +
                    proLbsCultivation * 12 +
                    ((baseResults.modelRevenue + proRevenueIncrease * 12) *
                        0.01) /
                        wholesalePrice) *
                    (baseResults.lbsProduced * costToProduce +
                        proLbsCultivation * 12 * (costToProduce / 5))) /
                    (baseResults.lbsProduced +
                        proLbsCultivation * 12 +
                        ((baseResults.modelRevenue + proRevenueIncrease * 12) *
                            0.01) /
                            wholesalePrice) -
                baseResults.profit) +
            ((baseResults.lbsProduced +
                proLbsCultivation * 12 +
                ((baseResults.modelRevenue + proRevenueIncrease * 12) * 0.01) /
                    wholesalePrice) *
                wholesalePrice -
                ((baseResults.lbsProduced +
                    proLbsCultivation * 12 +
                    ((baseResults.modelRevenue + proRevenueIncrease * 12) *
                        0.01) /
                        wholesalePrice) *
                    (baseResults.lbsProduced * costToProduce +
                        proLbsCultivation * 12 * (costToProduce / 5))) /
                    (baseResults.lbsProduced +
                        proLbsCultivation * 12 +
                        ((baseResults.modelRevenue + proRevenueIncrease * 12) *
                            0.01) /
                            wholesalePrice) -
                baseResults.profit) +
            ((baseResults.lbsProduced +
                proLbsCultivation * 12 +
                ((baseResults.modelRevenue + proRevenueIncrease * 12) * 0.01) /
                    wholesalePrice) *
                wholesalePrice -
                ((baseResults.lbsProduced +
                    proLbsCultivation * 12 +
                    ((baseResults.modelRevenue + proRevenueIncrease * 12) *
                        0.01) /
                        wholesalePrice) *
                    (baseResults.lbsProduced * costToProduce +
                        proLbsCultivation * 12 * (costToProduce / 5))) /
                    (baseResults.lbsProduced +
                        proLbsCultivation * 12 +
                        ((baseResults.modelRevenue + proRevenueIncrease * 12) *
                            0.01) /
                            wholesalePrice) -
                baseResults.profit) +
            ((baseResults.lbsProduced +
                proLbsCultivation * 12 +
                ((baseResults.modelRevenue + proRevenueIncrease * 12) * 0.01) /
                    wholesalePrice) *
                wholesalePrice -
                ((baseResults.lbsProduced +
                    proLbsCultivation * 12 +
                    ((baseResults.modelRevenue + proRevenueIncrease * 12) *
                        0.01) /
                        wholesalePrice) *
                    (baseResults.lbsProduced * costToProduce +
                        proLbsCultivation * 12 * (costToProduce / 5))) /
                    (baseResults.lbsProduced +
                        proLbsCultivation * 12 +
                        ((baseResults.modelRevenue + proRevenueIncrease * 12) *
                            0.01) /
                            wholesalePrice) -
                baseResults.profit) +
            ((baseResults.lbsProduced +
                proLbsCultivation * 12 +
                ((baseResults.modelRevenue + proRevenueIncrease * 12) * 0.01) /
                    wholesalePrice) *
                wholesalePrice -
                ((baseResults.lbsProduced +
                    proLbsCultivation * 12 +
                    ((baseResults.modelRevenue + proRevenueIncrease * 12) *
                        0.01) /
                        wholesalePrice) *
                    (baseResults.lbsProduced * costToProduce +
                        proLbsCultivation * 12 * (costToProduce / 5))) /
                    (baseResults.lbsProduced +
                        proLbsCultivation * 12 +
                        ((baseResults.modelRevenue + proRevenueIncrease * 12) *
                            0.01) /
                            wholesalePrice) -
                baseResults.profit) +
            ((baseResults.lbsProduced +
                proLbsCultivation * 12 +
                ((baseResults.modelRevenue + proRevenueIncrease * 12) * 0.01) /
                    wholesalePrice) *
                wholesalePrice -
                ((baseResults.lbsProduced +
                    proLbsCultivation * 12 +
                    ((baseResults.modelRevenue + proRevenueIncrease * 12) *
                        0.01) /
                        wholesalePrice) *
                    (baseResults.lbsProduced * costToProduce +
                        proLbsCultivation * 12 * (costToProduce / 5))) /
                    (baseResults.lbsProduced +
                        proLbsCultivation * 12 +
                        ((baseResults.modelRevenue + proRevenueIncrease * 12) *
                            0.01) /
                            wholesalePrice) -
                baseResults.profit),
        proYearThree:
            (baseResults.lbsProduced +
                proLbsCultivation * 12 +
                ((baseResults.modelRevenue + proRevenueIncrease * 12) * 0.01) /
                    wholesalePrice) *
                wholesalePrice -
            ((baseResults.lbsProduced +
                proLbsCultivation * 12 +
                ((baseResults.modelRevenue + proRevenueIncrease * 12) * 0.01) /
                    wholesalePrice) *
                (baseResults.lbsProduced * costToProduce +
                    proLbsCultivation * 12 * (costToProduce / 5))) /
                (baseResults.lbsProduced +
                    proLbsCultivation * 12 +
                    ((baseResults.modelRevenue + proRevenueIncrease * 12) *
                        0.01) /
                        wholesalePrice) -
            baseResults.profit +
            ((baseResults.lbsProduced +
                proLbsCultivation * 12 +
                ((baseResults.modelRevenue + proRevenueIncrease * 12) * 0.01) /
                    wholesalePrice) *
                wholesalePrice -
                ((baseResults.lbsProduced +
                    proLbsCultivation * 12 +
                    ((baseResults.modelRevenue + proRevenueIncrease * 12) *
                        0.01) /
                        wholesalePrice) *
                    (baseResults.lbsProduced * costToProduce +
                        proLbsCultivation * 12 * (costToProduce / 5))) /
                    (baseResults.lbsProduced +
                        proLbsCultivation * 12 +
                        ((baseResults.modelRevenue + proRevenueIncrease * 12) *
                            0.01) /
                            wholesalePrice) -
                baseResults.profit) +
            ((baseResults.lbsProduced +
                proLbsCultivation * 12 +
                ((baseResults.modelRevenue + proRevenueIncrease * 12) * 0.01) /
                    wholesalePrice) *
                wholesalePrice -
                ((baseResults.lbsProduced +
                    proLbsCultivation * 12 +
                    ((baseResults.modelRevenue + proRevenueIncrease * 12) *
                        0.01) /
                        wholesalePrice) *
                    (baseResults.lbsProduced * costToProduce +
                        proLbsCultivation * 12 * (costToProduce / 5))) /
                    (baseResults.lbsProduced +
                        proLbsCultivation * 12 +
                        ((baseResults.modelRevenue + proRevenueIncrease * 12) *
                            0.01) /
                            wholesalePrice) -
                baseResults.profit) +
            ((baseResults.lbsProduced +
                proLbsCultivation * 12 +
                ((baseResults.modelRevenue + proRevenueIncrease * 12) * 0.01) /
                    wholesalePrice) *
                wholesalePrice -
                ((baseResults.lbsProduced +
                    proLbsCultivation * 12 +
                    ((baseResults.modelRevenue + proRevenueIncrease * 12) *
                        0.01) /
                        wholesalePrice) *
                    (baseResults.lbsProduced * costToProduce +
                        proLbsCultivation * 12 * (costToProduce / 5))) /
                    (baseResults.lbsProduced +
                        proLbsCultivation * 12 +
                        ((baseResults.modelRevenue + proRevenueIncrease * 12) *
                            0.01) /
                            wholesalePrice) -
                baseResults.profit) +
            ((baseResults.lbsProduced +
                proLbsCultivation * 12 +
                ((baseResults.modelRevenue + proRevenueIncrease * 12) * 0.01) /
                    wholesalePrice) *
                wholesalePrice -
                ((baseResults.lbsProduced +
                    proLbsCultivation * 12 +
                    ((baseResults.modelRevenue + proRevenueIncrease * 12) *
                        0.01) /
                        wholesalePrice) *
                    (baseResults.lbsProduced * costToProduce +
                        proLbsCultivation * 12 * (costToProduce / 5))) /
                    (baseResults.lbsProduced +
                        proLbsCultivation * 12 +
                        ((baseResults.modelRevenue + proRevenueIncrease * 12) *
                            0.01) /
                            wholesalePrice) -
                baseResults.profit) +
            ((baseResults.lbsProduced +
                proLbsCultivation * 12 +
                ((baseResults.modelRevenue + proRevenueIncrease * 12) * 0.01) /
                    wholesalePrice) *
                wholesalePrice -
                ((baseResults.lbsProduced +
                    proLbsCultivation * 12 +
                    ((baseResults.modelRevenue + proRevenueIncrease * 12) *
                        0.01) /
                        wholesalePrice) *
                    (baseResults.lbsProduced * costToProduce +
                        proLbsCultivation * 12 * (costToProduce / 5))) /
                    (baseResults.lbsProduced +
                        proLbsCultivation * 12 +
                        ((baseResults.modelRevenue + proRevenueIncrease * 12) *
                            0.01) /
                            wholesalePrice) -
                baseResults.profit) +
            ((baseResults.lbsProduced +
                proLbsCultivation * 12 +
                ((baseResults.modelRevenue + proRevenueIncrease * 12) * 0.01) /
                    wholesalePrice) *
                wholesalePrice -
                ((baseResults.lbsProduced +
                    proLbsCultivation * 12 +
                    ((baseResults.modelRevenue + proRevenueIncrease * 12) *
                        0.01) /
                        wholesalePrice) *
                    (baseResults.lbsProduced * costToProduce +
                        proLbsCultivation * 12 * (costToProduce / 5))) /
                    (baseResults.lbsProduced +
                        proLbsCultivation * 12 +
                        ((baseResults.modelRevenue + proRevenueIncrease * 12) *
                            0.01) /
                            wholesalePrice) -
                baseResults.profit) +
            ((baseResults.lbsProduced +
                proLbsCultivation * 12 +
                ((baseResults.modelRevenue + proRevenueIncrease * 12) * 0.01) /
                    wholesalePrice) *
                wholesalePrice -
                ((baseResults.lbsProduced +
                    proLbsCultivation * 12 +
                    ((baseResults.modelRevenue + proRevenueIncrease * 12) *
                        0.01) /
                        wholesalePrice) *
                    (baseResults.lbsProduced * costToProduce +
                        proLbsCultivation * 12 * (costToProduce / 5))) /
                    (baseResults.lbsProduced +
                        proLbsCultivation * 12 +
                        ((baseResults.modelRevenue + proRevenueIncrease * 12) *
                            0.01) /
                            wholesalePrice) -
                baseResults.profit) +
            ((baseResults.lbsProduced +
                proLbsCultivation * 12 +
                ((baseResults.modelRevenue + proRevenueIncrease * 12) * 0.01) /
                    wholesalePrice) *
                wholesalePrice -
                ((baseResults.lbsProduced +
                    proLbsCultivation * 12 +
                    ((baseResults.modelRevenue + proRevenueIncrease * 12) *
                        0.01) /
                        wholesalePrice) *
                    (baseResults.lbsProduced * costToProduce +
                        proLbsCultivation * 12 * (costToProduce / 5))) /
                    (baseResults.lbsProduced +
                        proLbsCultivation * 12 +
                        ((baseResults.modelRevenue + proRevenueIncrease * 12) *
                            0.01) /
                            wholesalePrice) -
                baseResults.profit) +
            ((baseResults.lbsProduced +
                proLbsCultivation * 12 +
                ((baseResults.modelRevenue + proRevenueIncrease * 12) * 0.01) /
                    wholesalePrice) *
                wholesalePrice -
                ((baseResults.lbsProduced +
                    proLbsCultivation * 12 +
                    ((baseResults.modelRevenue + proRevenueIncrease * 12) *
                        0.01) /
                        wholesalePrice) *
                    (baseResults.lbsProduced * costToProduce +
                        proLbsCultivation * 12 * (costToProduce / 5))) /
                    (baseResults.lbsProduced +
                        proLbsCultivation * 12 +
                        ((baseResults.modelRevenue + proRevenueIncrease * 12) *
                            0.01) /
                            wholesalePrice) -
                baseResults.profit) +
            ((baseResults.lbsProduced +
                proLbsCultivation * 12 +
                ((baseResults.modelRevenue + proRevenueIncrease * 12) * 0.01) /
                    wholesalePrice) *
                wholesalePrice -
                ((baseResults.lbsProduced +
                    proLbsCultivation * 12 +
                    ((baseResults.modelRevenue + proRevenueIncrease * 12) *
                        0.01) /
                        wholesalePrice) *
                    (baseResults.lbsProduced * costToProduce +
                        proLbsCultivation * 12 * (costToProduce / 5))) /
                    (baseResults.lbsProduced +
                        proLbsCultivation * 12 +
                        ((baseResults.modelRevenue + proRevenueIncrease * 12) *
                            0.01) /
                            wholesalePrice) -
                baseResults.profit) +
            ((baseResults.lbsProduced +
                proLbsCultivation * 12 +
                ((baseResults.modelRevenue + proRevenueIncrease * 12) * 0.01) /
                    wholesalePrice) *
                wholesalePrice -
                ((baseResults.lbsProduced +
                    proLbsCultivation * 12 +
                    ((baseResults.modelRevenue + proRevenueIncrease * 12) *
                        0.01) /
                        wholesalePrice) *
                    (baseResults.lbsProduced * costToProduce +
                        proLbsCultivation * 12 * (costToProduce / 5))) /
                    (baseResults.lbsProduced +
                        proLbsCultivation * 12 +
                        ((baseResults.modelRevenue + proRevenueIncrease * 12) *
                            0.01) /
                            wholesalePrice) -
                baseResults.profit),
        eliteYearOne:
            (baseResults.lbsProduced + 0 + 0 / wholesalePrice) *
                wholesalePrice -
            ((baseResults.lbsProduced + 0 + 0 / wholesalePrice) *
                (baseResults.lbsProduced * costToProduce +
                    0 * (costToProduce / 5))) /
                (baseResults.lbsProduced + 0 + 0 / wholesalePrice) -
            baseResults.profit +
            ((baseResults.lbsProduced +
                0 +
                ((baseResults.modelRevenue + 0) * 0.01) / wholesalePrice) *
                wholesalePrice -
                ((baseResults.lbsProduced +
                    0 +
                    ((baseResults.modelRevenue + 0) * 0.01) / wholesalePrice) *
                    (baseResults.lbsProduced * costToProduce +
                        0 * (costToProduce / 5))) /
                    (baseResults.lbsProduced +
                        0 +
                        ((baseResults.modelRevenue + 0) * 0.01) /
                            wholesalePrice) -
                baseResults.profit) +
            ((baseResults.lbsProduced +
                0 +
                ((baseResults.modelRevenue + 0) * 0.01) / wholesalePrice) *
                wholesalePrice -
                ((baseResults.lbsProduced +
                    0 +
                    ((baseResults.modelRevenue + 0) * 0.01) / wholesalePrice) *
                    (baseResults.lbsProduced * costToProduce +
                        0 * (costToProduce / 5))) /
                    (baseResults.lbsProduced +
                        0 +
                        ((baseResults.modelRevenue + 0) * 0.01) /
                            wholesalePrice) -
                baseResults.profit) +
            ((baseResults.lbsProduced +
                eliteLbsCultivation +
                ((baseResults.modelRevenue + eliteRevenueIncrease) * 0.01) /
                    wholesalePrice) *
                wholesalePrice -
                ((baseResults.lbsProduced +
                    eliteLbsCultivation +
                    ((baseResults.modelRevenue + eliteRevenueIncrease) * 0.01) /
                        wholesalePrice) *
                    (baseResults.lbsProduced * costToProduce +
                        eliteLbsCultivation * (costToProduce / 5))) /
                    (baseResults.lbsProduced +
                        eliteLbsCultivation +
                        ((baseResults.modelRevenue + eliteRevenueIncrease) *
                            0.01) /
                            wholesalePrice) -
                baseResults.profit) +
            ((baseResults.lbsProduced +
                eliteLbsCultivation * 2 +
                ((baseResults.modelRevenue + eliteRevenueIncrease * 2) * 0.01) /
                    wholesalePrice) *
                wholesalePrice -
                ((baseResults.lbsProduced +
                    eliteLbsCultivation * 2 +
                    ((baseResults.modelRevenue + eliteRevenueIncrease * 2) *
                        0.01) /
                        wholesalePrice) *
                    (baseResults.lbsProduced * costToProduce +
                        eliteLbsCultivation * 2 * (costToProduce / 5))) /
                    (baseResults.lbsProduced +
                        eliteLbsCultivation * 2 +
                        ((baseResults.modelRevenue + eliteRevenueIncrease * 2) *
                            0.01) /
                            wholesalePrice) -
                baseResults.profit) +
            ((baseResults.lbsProduced +
                eliteLbsCultivation * 3 +
                ((baseResults.modelRevenue + eliteRevenueIncrease * 3) * 0.01) /
                    wholesalePrice) *
                wholesalePrice -
                ((baseResults.lbsProduced +
                    eliteLbsCultivation * 3 +
                    ((baseResults.modelRevenue + eliteRevenueIncrease * 3) *
                        0.01) /
                        wholesalePrice) *
                    (baseResults.lbsProduced * costToProduce +
                        eliteLbsCultivation * 3 * (costToProduce / 5))) /
                    (baseResults.lbsProduced +
                        eliteLbsCultivation * 3 +
                        ((baseResults.modelRevenue + eliteRevenueIncrease * 3) *
                            0.01) /
                            wholesalePrice) -
                baseResults.profit) +
            ((baseResults.lbsProduced +
                eliteLbsCultivation * 4 +
                ((baseResults.modelRevenue + eliteRevenueIncrease * 4) * 0.01) /
                    wholesalePrice) *
                wholesalePrice -
                ((baseResults.lbsProduced +
                    eliteLbsCultivation * 4 +
                    ((baseResults.modelRevenue + eliteRevenueIncrease * 4) *
                        0.01) /
                        wholesalePrice) *
                    (baseResults.lbsProduced * costToProduce +
                        eliteLbsCultivation * 4 * (costToProduce / 5))) /
                    (baseResults.lbsProduced +
                        eliteLbsCultivation * 4 +
                        ((baseResults.modelRevenue + eliteRevenueIncrease * 4) *
                            0.01) /
                            wholesalePrice) -
                baseResults.profit) +
            ((baseResults.lbsProduced +
                eliteLbsCultivation * 5 +
                ((baseResults.modelRevenue + eliteRevenueIncrease * 5) * 0.01) /
                    wholesalePrice) *
                wholesalePrice -
                ((baseResults.lbsProduced +
                    eliteLbsCultivation * 5 +
                    ((baseResults.modelRevenue + eliteRevenueIncrease * 5) *
                        0.01) /
                        wholesalePrice) *
                    (baseResults.lbsProduced * costToProduce +
                        eliteLbsCultivation * 5 * (costToProduce / 5))) /
                    (baseResults.lbsProduced +
                        eliteLbsCultivation * 5 +
                        ((baseResults.modelRevenue + eliteRevenueIncrease * 5) *
                            0.01) /
                            wholesalePrice) -
                baseResults.profit) +
            ((baseResults.lbsProduced +
                eliteLbsCultivation * 6 +
                ((baseResults.modelRevenue + eliteRevenueIncrease * 6) * 0.01) /
                    wholesalePrice) *
                wholesalePrice -
                ((baseResults.lbsProduced +
                    eliteLbsCultivation * 6 +
                    ((baseResults.modelRevenue + eliteRevenueIncrease * 6) *
                        0.01) /
                        wholesalePrice) *
                    (baseResults.lbsProduced * costToProduce +
                        eliteLbsCultivation * 6 * (costToProduce / 5))) /
                    (baseResults.lbsProduced +
                        eliteLbsCultivation * 6 +
                        ((baseResults.modelRevenue + eliteRevenueIncrease * 6) *
                            0.01) /
                            wholesalePrice) -
                baseResults.profit) +
            ((baseResults.lbsProduced +
                eliteLbsCultivation * 7 +
                ((baseResults.modelRevenue + eliteRevenueIncrease * 7) * 0.01) /
                    wholesalePrice) *
                wholesalePrice -
                ((baseResults.lbsProduced +
                    eliteLbsCultivation * 7 +
                    ((baseResults.modelRevenue + eliteRevenueIncrease * 7) *
                        0.01) /
                        wholesalePrice) *
                    (baseResults.lbsProduced * costToProduce +
                        eliteLbsCultivation * 7 * (costToProduce / 5))) /
                    (baseResults.lbsProduced +
                        eliteLbsCultivation * 7 +
                        ((baseResults.modelRevenue + eliteRevenueIncrease * 7) *
                            0.01) /
                            wholesalePrice) -
                baseResults.profit) +
            ((baseResults.lbsProduced +
                eliteLbsCultivation * 8 +
                ((baseResults.modelRevenue + eliteRevenueIncrease * 8) * 0.01) /
                    wholesalePrice) *
                wholesalePrice -
                ((baseResults.lbsProduced +
                    eliteLbsCultivation * 8 +
                    ((baseResults.modelRevenue + eliteRevenueIncrease * 8) *
                        0.01) /
                        wholesalePrice) *
                    (baseResults.lbsProduced * costToProduce +
                        eliteLbsCultivation * 8 * (costToProduce / 5))) /
                    (baseResults.lbsProduced +
                        eliteLbsCultivation * 8 +
                        ((baseResults.modelRevenue + eliteRevenueIncrease * 8) *
                            0.01) /
                            wholesalePrice) -
                baseResults.profit) +
            ((baseResults.lbsProduced +
                eliteLbsCultivation * 9 +
                ((baseResults.modelRevenue + eliteRevenueIncrease * 9) * 0.01) /
                    wholesalePrice) *
                wholesalePrice -
                ((baseResults.lbsProduced +
                    eliteLbsCultivation * 9 +
                    ((baseResults.modelRevenue + eliteRevenueIncrease * 9) *
                        0.01) /
                        wholesalePrice) *
                    (baseResults.lbsProduced * costToProduce +
                        eliteLbsCultivation * 9 * (costToProduce / 5))) /
                    (baseResults.lbsProduced +
                        eliteLbsCultivation * 9 +
                        ((baseResults.modelRevenue + eliteRevenueIncrease * 9) *
                            0.01) /
                            wholesalePrice) -
                baseResults.profit),
        eliteYearTwo:
            (baseResults.lbsProduced +
                eliteLbsCultivation * 10 +
                ((baseResults.modelRevenue + eliteRevenueIncrease * 10) *
                    0.01) /
                    wholesalePrice) *
                wholesalePrice -
            ((baseResults.lbsProduced +
                eliteLbsCultivation * 10 +
                ((baseResults.modelRevenue + eliteRevenueIncrease * 10) *
                    0.01) /
                    wholesalePrice) *
                (baseResults.lbsProduced * costToProduce +
                    eliteLbsCultivation * 10 * (costToProduce / 5))) /
                (baseResults.lbsProduced +
                    eliteLbsCultivation * 10 +
                    ((baseResults.modelRevenue + eliteRevenueIncrease * 10) *
                        0.01) /
                        wholesalePrice) -
            baseResults.profit +
            ((baseResults.lbsProduced +
                eliteLbsCultivation * 11 +
                ((baseResults.modelRevenue + eliteRevenueIncrease * 11) *
                    0.01) /
                    wholesalePrice) *
                wholesalePrice -
                ((baseResults.lbsProduced +
                    eliteLbsCultivation * 11 +
                    ((baseResults.modelRevenue + eliteRevenueIncrease * 11) *
                        0.01) /
                        wholesalePrice) *
                    (baseResults.lbsProduced * costToProduce +
                        eliteLbsCultivation * 11 * (costToProduce / 5))) /
                    (baseResults.lbsProduced +
                        eliteLbsCultivation * 11 +
                        ((baseResults.modelRevenue +
                            eliteRevenueIncrease * 11) *
                            0.01) /
                            wholesalePrice) -
                baseResults.profit) +
            ((baseResults.lbsProduced +
                eliteLbsCultivation * 12 +
                ((baseResults.modelRevenue + eliteRevenueIncrease * 12) *
                    0.01) /
                    wholesalePrice) *
                wholesalePrice -
                ((baseResults.lbsProduced +
                    eliteLbsCultivation * 12 +
                    ((baseResults.modelRevenue + eliteRevenueIncrease * 12) *
                        0.01) /
                        wholesalePrice) *
                    (baseResults.lbsProduced * costToProduce +
                        eliteLbsCultivation * 12 * (costToProduce / 5))) /
                    (baseResults.lbsProduced +
                        eliteLbsCultivation * 12 +
                        ((baseResults.modelRevenue +
                            eliteRevenueIncrease * 12) *
                            0.01) /
                            wholesalePrice) -
                baseResults.profit) +
            ((baseResults.lbsProduced +
                eliteLbsCultivation * 12 +
                ((baseResults.modelRevenue + eliteRevenueIncrease * 12) *
                    0.01) /
                    wholesalePrice) *
                wholesalePrice -
                ((baseResults.lbsProduced +
                    eliteLbsCultivation * 12 +
                    ((baseResults.modelRevenue + eliteRevenueIncrease * 12) *
                        0.01) /
                        wholesalePrice) *
                    (baseResults.lbsProduced * costToProduce +
                        eliteLbsCultivation * 12 * (costToProduce / 5))) /
                    (baseResults.lbsProduced +
                        eliteLbsCultivation * 12 +
                        ((baseResults.modelRevenue +
                            eliteRevenueIncrease * 12) *
                            0.01) /
                            wholesalePrice) -
                baseResults.profit) +
            ((baseResults.lbsProduced +
                eliteLbsCultivation * 12 +
                ((baseResults.modelRevenue + eliteRevenueIncrease * 12) *
                    0.01) /
                    wholesalePrice) *
                wholesalePrice -
                ((baseResults.lbsProduced +
                    eliteLbsCultivation * 12 +
                    ((baseResults.modelRevenue + eliteRevenueIncrease * 12) *
                        0.01) /
                        wholesalePrice) *
                    (baseResults.lbsProduced * costToProduce +
                        eliteLbsCultivation * 12 * (costToProduce / 5))) /
                    (baseResults.lbsProduced +
                        eliteLbsCultivation * 12 +
                        ((baseResults.modelRevenue +
                            eliteRevenueIncrease * 12) *
                            0.01) /
                            wholesalePrice) -
                baseResults.profit) +
            ((baseResults.lbsProduced +
                eliteLbsCultivation * 12 +
                ((baseResults.modelRevenue + eliteRevenueIncrease * 12) *
                    0.01) /
                    wholesalePrice) *
                wholesalePrice -
                ((baseResults.lbsProduced +
                    eliteLbsCultivation * 12 +
                    ((baseResults.modelRevenue + eliteRevenueIncrease * 12) *
                        0.01) /
                        wholesalePrice) *
                    (baseResults.lbsProduced * costToProduce +
                        eliteLbsCultivation * 12 * (costToProduce / 5))) /
                    (baseResults.lbsProduced +
                        eliteLbsCultivation * 12 +
                        ((baseResults.modelRevenue +
                            eliteRevenueIncrease * 12) *
                            0.01) /
                            wholesalePrice) -
                baseResults.profit) +
            ((baseResults.lbsProduced +
                eliteLbsCultivation * 12 +
                ((baseResults.modelRevenue + eliteRevenueIncrease * 12) *
                    0.01) /
                    wholesalePrice) *
                wholesalePrice -
                ((baseResults.lbsProduced +
                    eliteLbsCultivation * 12 +
                    ((baseResults.modelRevenue + eliteRevenueIncrease * 12) *
                        0.01) /
                        wholesalePrice) *
                    (baseResults.lbsProduced * costToProduce +
                        eliteLbsCultivation * 12 * (costToProduce / 5))) /
                    (baseResults.lbsProduced +
                        eliteLbsCultivation * 12 +
                        ((baseResults.modelRevenue +
                            eliteRevenueIncrease * 12) *
                            0.01) /
                            wholesalePrice) -
                baseResults.profit) +
            ((baseResults.lbsProduced +
                eliteLbsCultivation * 12 +
                ((baseResults.modelRevenue + eliteRevenueIncrease * 12) *
                    0.01) /
                    wholesalePrice) *
                wholesalePrice -
                ((baseResults.lbsProduced +
                    eliteLbsCultivation * 12 +
                    ((baseResults.modelRevenue + eliteRevenueIncrease * 12) *
                        0.01) /
                        wholesalePrice) *
                    (baseResults.lbsProduced * costToProduce +
                        eliteLbsCultivation * 12 * (costToProduce / 5))) /
                    (baseResults.lbsProduced +
                        eliteLbsCultivation * 12 +
                        ((baseResults.modelRevenue +
                            eliteRevenueIncrease * 12) *
                            0.01) /
                            wholesalePrice) -
                baseResults.profit) +
            ((baseResults.lbsProduced +
                eliteLbsCultivation * 12 +
                ((baseResults.modelRevenue + eliteRevenueIncrease * 12) *
                    0.01) /
                    wholesalePrice) *
                wholesalePrice -
                ((baseResults.lbsProduced +
                    eliteLbsCultivation * 12 +
                    ((baseResults.modelRevenue + eliteRevenueIncrease * 12) *
                        0.01) /
                        wholesalePrice) *
                    (baseResults.lbsProduced * costToProduce +
                        eliteLbsCultivation * 12 * (costToProduce / 5))) /
                    (baseResults.lbsProduced +
                        eliteLbsCultivation * 12 +
                        ((baseResults.modelRevenue +
                            eliteRevenueIncrease * 12) *
                            0.01) /
                            wholesalePrice) -
                baseResults.profit) +
            ((baseResults.lbsProduced +
                eliteLbsCultivation * 12 +
                ((baseResults.modelRevenue + eliteRevenueIncrease * 12) *
                    0.01) /
                    wholesalePrice) *
                wholesalePrice -
                ((baseResults.lbsProduced +
                    eliteLbsCultivation * 12 +
                    ((baseResults.modelRevenue + eliteRevenueIncrease * 12) *
                        0.01) /
                        wholesalePrice) *
                    (baseResults.lbsProduced * costToProduce +
                        eliteLbsCultivation * 12 * (costToProduce / 5))) /
                    (baseResults.lbsProduced +
                        eliteLbsCultivation * 12 +
                        ((baseResults.modelRevenue +
                            eliteRevenueIncrease * 12) *
                            0.01) /
                            wholesalePrice) -
                baseResults.profit) +
            ((baseResults.lbsProduced +
                eliteLbsCultivation * 12 +
                ((baseResults.modelRevenue + eliteRevenueIncrease * 12) *
                    0.01) /
                    wholesalePrice) *
                wholesalePrice -
                ((baseResults.lbsProduced +
                    eliteLbsCultivation * 12 +
                    ((baseResults.modelRevenue + eliteRevenueIncrease * 12) *
                        0.01) /
                        wholesalePrice) *
                    (baseResults.lbsProduced * costToProduce +
                        eliteLbsCultivation * 12 * (costToProduce / 5))) /
                    (baseResults.lbsProduced +
                        eliteLbsCultivation * 12 +
                        ((baseResults.modelRevenue +
                            eliteRevenueIncrease * 12) *
                            0.01) /
                            wholesalePrice) -
                baseResults.profit) +
            ((baseResults.lbsProduced +
                eliteLbsCultivation * 12 +
                ((baseResults.modelRevenue + eliteRevenueIncrease * 12) *
                    0.01) /
                    wholesalePrice) *
                wholesalePrice -
                ((baseResults.lbsProduced +
                    eliteLbsCultivation * 12 +
                    ((baseResults.modelRevenue + eliteRevenueIncrease * 12) *
                        0.01) /
                        wholesalePrice) *
                    (baseResults.lbsProduced * costToProduce +
                        eliteLbsCultivation * 12 * (costToProduce / 5))) /
                    (baseResults.lbsProduced +
                        eliteLbsCultivation * 12 +
                        ((baseResults.modelRevenue +
                            eliteRevenueIncrease * 12) *
                            0.01) /
                            wholesalePrice) -
                baseResults.profit),
        eliteYearThree:
            (baseResults.lbsProduced +
                eliteLbsCultivation * 12 +
                ((baseResults.modelRevenue + eliteRevenueIncrease * 12) *
                    0.01) /
                    wholesalePrice) *
                wholesalePrice -
            ((baseResults.lbsProduced +
                eliteLbsCultivation * 12 +
                ((baseResults.modelRevenue + eliteRevenueIncrease * 12) *
                    0.01) /
                    wholesalePrice) *
                (baseResults.lbsProduced * costToProduce +
                    eliteLbsCultivation * 12 * (costToProduce / 5))) /
                (baseResults.lbsProduced +
                    eliteLbsCultivation * 12 +
                    ((baseResults.modelRevenue + eliteRevenueIncrease * 12) *
                        0.01) /
                        wholesalePrice) -
            baseResults.profit +
            ((baseResults.lbsProduced +
                eliteLbsCultivation * 12 +
                ((baseResults.modelRevenue + eliteRevenueIncrease * 12) *
                    0.01) /
                    wholesalePrice) *
                wholesalePrice -
                ((baseResults.lbsProduced +
                    eliteLbsCultivation * 12 +
                    ((baseResults.modelRevenue + eliteRevenueIncrease * 12) *
                        0.01) /
                        wholesalePrice) *
                    (baseResults.lbsProduced * costToProduce +
                        eliteLbsCultivation * 12 * (costToProduce / 5))) /
                    (baseResults.lbsProduced +
                        eliteLbsCultivation * 12 +
                        ((baseResults.modelRevenue +
                            eliteRevenueIncrease * 12) *
                            0.01) /
                            wholesalePrice) -
                baseResults.profit) +
            ((baseResults.lbsProduced +
                eliteLbsCultivation * 12 +
                ((baseResults.modelRevenue + eliteRevenueIncrease * 12) *
                    0.01) /
                    wholesalePrice) *
                wholesalePrice -
                ((baseResults.lbsProduced +
                    eliteLbsCultivation * 12 +
                    ((baseResults.modelRevenue + eliteRevenueIncrease * 12) *
                        0.01) /
                        wholesalePrice) *
                    (baseResults.lbsProduced * costToProduce +
                        eliteLbsCultivation * 12 * (costToProduce / 5))) /
                    (baseResults.lbsProduced +
                        eliteLbsCultivation * 12 +
                        ((baseResults.modelRevenue +
                            eliteRevenueIncrease * 12) *
                            0.01) /
                            wholesalePrice) -
                baseResults.profit) +
            ((baseResults.lbsProduced +
                eliteLbsCultivation * 12 +
                ((baseResults.modelRevenue + eliteRevenueIncrease * 12) *
                    0.01) /
                    wholesalePrice) *
                wholesalePrice -
                ((baseResults.lbsProduced +
                    eliteLbsCultivation * 12 +
                    ((baseResults.modelRevenue + eliteRevenueIncrease * 12) *
                        0.01) /
                        wholesalePrice) *
                    (baseResults.lbsProduced * costToProduce +
                        eliteLbsCultivation * 12 * (costToProduce / 5))) /
                    (baseResults.lbsProduced +
                        eliteLbsCultivation * 12 +
                        ((baseResults.modelRevenue +
                            eliteRevenueIncrease * 12) *
                            0.01) /
                            wholesalePrice) -
                baseResults.profit) +
            ((baseResults.lbsProduced +
                eliteLbsCultivation * 12 +
                ((baseResults.modelRevenue + eliteRevenueIncrease * 12) *
                    0.01) /
                    wholesalePrice) *
                wholesalePrice -
                ((baseResults.lbsProduced +
                    eliteLbsCultivation * 12 +
                    ((baseResults.modelRevenue + eliteRevenueIncrease * 12) *
                        0.01) /
                        wholesalePrice) *
                    (baseResults.lbsProduced * costToProduce +
                        eliteLbsCultivation * 12 * (costToProduce / 5))) /
                    (baseResults.lbsProduced +
                        eliteLbsCultivation * 12 +
                        ((baseResults.modelRevenue +
                            eliteRevenueIncrease * 12) *
                            0.01) /
                            wholesalePrice) -
                baseResults.profit) +
            ((baseResults.lbsProduced +
                eliteLbsCultivation * 12 +
                ((baseResults.modelRevenue + eliteRevenueIncrease * 12) *
                    0.01) /
                    wholesalePrice) *
                wholesalePrice -
                ((baseResults.lbsProduced +
                    eliteLbsCultivation * 12 +
                    ((baseResults.modelRevenue + eliteRevenueIncrease * 12) *
                        0.01) /
                        wholesalePrice) *
                    (baseResults.lbsProduced * costToProduce +
                        eliteLbsCultivation * 12 * (costToProduce / 5))) /
                    (baseResults.lbsProduced +
                        eliteLbsCultivation * 12 +
                        ((baseResults.modelRevenue +
                            eliteRevenueIncrease * 12) *
                            0.01) /
                            wholesalePrice) -
                baseResults.profit) +
            ((baseResults.lbsProduced +
                eliteLbsCultivation * 12 +
                ((baseResults.modelRevenue + eliteRevenueIncrease * 12) *
                    0.01) /
                    wholesalePrice) *
                wholesalePrice -
                ((baseResults.lbsProduced +
                    eliteLbsCultivation * 12 +
                    ((baseResults.modelRevenue + eliteRevenueIncrease * 12) *
                        0.01) /
                        wholesalePrice) *
                    (baseResults.lbsProduced * costToProduce +
                        eliteLbsCultivation * 12 * (costToProduce / 5))) /
                    (baseResults.lbsProduced +
                        eliteLbsCultivation * 12 +
                        ((baseResults.modelRevenue +
                            eliteRevenueIncrease * 12) *
                            0.01) /
                            wholesalePrice) -
                baseResults.profit) +
            ((baseResults.lbsProduced +
                eliteLbsCultivation * 12 +
                ((baseResults.modelRevenue + eliteRevenueIncrease * 12) *
                    0.01) /
                    wholesalePrice) *
                wholesalePrice -
                ((baseResults.lbsProduced +
                    eliteLbsCultivation * 12 +
                    ((baseResults.modelRevenue + eliteRevenueIncrease * 12) *
                        0.01) /
                        wholesalePrice) *
                    (baseResults.lbsProduced * costToProduce +
                        eliteLbsCultivation * 12 * (costToProduce / 5))) /
                    (baseResults.lbsProduced +
                        eliteLbsCultivation * 12 +
                        ((baseResults.modelRevenue +
                            eliteRevenueIncrease * 12) *
                            0.01) /
                            wholesalePrice) -
                baseResults.profit) +
            ((baseResults.lbsProduced +
                eliteLbsCultivation * 12 +
                ((baseResults.modelRevenue + eliteRevenueIncrease * 12) *
                    0.01) /
                    wholesalePrice) *
                wholesalePrice -
                ((baseResults.lbsProduced +
                    eliteLbsCultivation * 12 +
                    ((baseResults.modelRevenue + eliteRevenueIncrease * 12) *
                        0.01) /
                        wholesalePrice) *
                    (baseResults.lbsProduced * costToProduce +
                        eliteLbsCultivation * 12 * (costToProduce / 5))) /
                    (baseResults.lbsProduced +
                        eliteLbsCultivation * 12 +
                        ((baseResults.modelRevenue +
                            eliteRevenueIncrease * 12) *
                            0.01) /
                            wholesalePrice) -
                baseResults.profit) +
            ((baseResults.lbsProduced +
                eliteLbsCultivation * 12 +
                ((baseResults.modelRevenue + eliteRevenueIncrease * 12) *
                    0.01) /
                    wholesalePrice) *
                wholesalePrice -
                ((baseResults.lbsProduced +
                    eliteLbsCultivation * 12 +
                    ((baseResults.modelRevenue + eliteRevenueIncrease * 12) *
                        0.01) /
                        wholesalePrice) *
                    (baseResults.lbsProduced * costToProduce +
                        eliteLbsCultivation * 12 * (costToProduce / 5))) /
                    (baseResults.lbsProduced +
                        eliteLbsCultivation * 12 +
                        ((baseResults.modelRevenue +
                            eliteRevenueIncrease * 12) *
                            0.01) /
                            wholesalePrice) -
                baseResults.profit) +
            ((baseResults.lbsProduced +
                eliteLbsCultivation * 12 +
                ((baseResults.modelRevenue + eliteRevenueIncrease * 12) *
                    0.01) /
                    wholesalePrice) *
                wholesalePrice -
                ((baseResults.lbsProduced +
                    eliteLbsCultivation * 12 +
                    ((baseResults.modelRevenue + eliteRevenueIncrease * 12) *
                        0.01) /
                        wholesalePrice) *
                    (baseResults.lbsProduced * costToProduce +
                        eliteLbsCultivation * 12 * (costToProduce / 5))) /
                    (baseResults.lbsProduced +
                        eliteLbsCultivation * 12 +
                        ((baseResults.modelRevenue +
                            eliteRevenueIncrease * 12) *
                            0.01) /
                            wholesalePrice) -
                baseResults.profit) +
            ((baseResults.lbsProduced +
                eliteLbsCultivation * 12 +
                ((baseResults.modelRevenue + eliteRevenueIncrease * 12) *
                    0.01) /
                    wholesalePrice) *
                wholesalePrice -
                ((baseResults.lbsProduced +
                    eliteLbsCultivation * 12 +
                    ((baseResults.modelRevenue + eliteRevenueIncrease * 12) *
                        0.01) /
                        wholesalePrice) *
                    (baseResults.lbsProduced * costToProduce +
                        eliteLbsCultivation * 12 * (costToProduce / 5))) /
                    (baseResults.lbsProduced +
                        eliteLbsCultivation * 12 +
                        ((baseResults.modelRevenue +
                            eliteRevenueIncrease * 12) *
                            0.01) /
                            wholesalePrice) -
                baseResults.profit),
        unlimitedYearOne:
            (baseResults.lbsProduced + 0 + 0 / wholesalePrice) *
                wholesalePrice -
            ((baseResults.lbsProduced + 0 + 0 / wholesalePrice) *
                (baseResults.lbsProduced * costToProduce +
                    0 * (costToProduce / 5))) /
                (baseResults.lbsProduced + 0 + 0 / wholesalePrice) -
            baseResults.profit +
            ((baseResults.lbsProduced +
                0 +
                ((baseResults.modelRevenue + 0) * 0.01) / wholesalePrice) *
                wholesalePrice -
                ((baseResults.lbsProduced +
                    0 +
                    ((baseResults.modelRevenue + 0) * 0.01) / wholesalePrice) *
                    (baseResults.lbsProduced * costToProduce +
                        0 * (costToProduce / 5))) /
                    (baseResults.lbsProduced +
                        0 +
                        ((baseResults.modelRevenue + 0) * 0.01) /
                            wholesalePrice) -
                baseResults.profit) +
            ((baseResults.lbsProduced +
                unlimitedLbsCultivation +
                ((baseResults.modelRevenue + unlimitedRevenueIncrease) * 0.01) /
                    wholesalePrice) *
                wholesalePrice -
                ((baseResults.lbsProduced +
                    unlimitedLbsCultivation +
                    ((baseResults.modelRevenue + unlimitedRevenueIncrease) *
                        0.01) /
                        wholesalePrice) *
                    (baseResults.lbsProduced * costToProduce +
                        unlimitedLbsCultivation * (costToProduce / 5))) /
                    (baseResults.lbsProduced +
                        unlimitedLbsCultivation +
                        ((baseResults.modelRevenue + unlimitedRevenueIncrease) *
                            0.01) /
                            wholesalePrice) -
                baseResults.profit) +
            ((baseResults.lbsProduced +
                unlimitedLbsCultivation * 2 +
                ((baseResults.modelRevenue + unlimitedRevenueIncrease * 2) *
                    0.01) /
                    wholesalePrice) *
                wholesalePrice -
                ((baseResults.lbsProduced +
                    unlimitedLbsCultivation * 2 +
                    ((baseResults.modelRevenue + unlimitedRevenueIncrease * 2) *
                        0.01) /
                        wholesalePrice) *
                    (baseResults.lbsProduced * costToProduce +
                        unlimitedLbsCultivation * 2 * (costToProduce / 5))) /
                    (baseResults.lbsProduced +
                        unlimitedLbsCultivation * 2 +
                        ((baseResults.modelRevenue +
                            unlimitedRevenueIncrease * 2) *
                            0.01) /
                            wholesalePrice) -
                baseResults.profit) +
            ((baseResults.lbsProduced +
                unlimitedLbsCultivation * 3 +
                ((baseResults.modelRevenue + unlimitedRevenueIncrease * 3) *
                    0.01) /
                    wholesalePrice) *
                wholesalePrice -
                ((baseResults.lbsProduced +
                    unlimitedLbsCultivation * 3 +
                    ((baseResults.modelRevenue + unlimitedRevenueIncrease * 3) *
                        0.01) /
                        wholesalePrice) *
                    (baseResults.lbsProduced * costToProduce +
                        unlimitedLbsCultivation * 3 * (costToProduce / 5))) /
                    (baseResults.lbsProduced +
                        unlimitedLbsCultivation * 3 +
                        ((baseResults.modelRevenue +
                            unlimitedRevenueIncrease * 3) *
                            0.01) /
                            wholesalePrice) -
                baseResults.profit) +
            ((baseResults.lbsProduced +
                unlimitedLbsCultivation * 4 +
                ((baseResults.modelRevenue + unlimitedRevenueIncrease * 4) *
                    0.01) /
                    wholesalePrice) *
                wholesalePrice -
                ((baseResults.lbsProduced +
                    unlimitedLbsCultivation * 4 +
                    ((baseResults.modelRevenue + unlimitedRevenueIncrease * 4) *
                        0.01) /
                        wholesalePrice) *
                    (baseResults.lbsProduced * costToProduce +
                        unlimitedLbsCultivation * 4 * (costToProduce / 5))) /
                    (baseResults.lbsProduced +
                        unlimitedLbsCultivation * 4 +
                        ((baseResults.modelRevenue +
                            unlimitedRevenueIncrease * 4) *
                            0.01) /
                            wholesalePrice) -
                baseResults.profit) +
            ((baseResults.lbsProduced +
                unlimitedLbsCultivation * 5 +
                ((baseResults.modelRevenue + unlimitedRevenueIncrease * 5) *
                    0.01) /
                    wholesalePrice) *
                wholesalePrice -
                ((baseResults.lbsProduced +
                    unlimitedLbsCultivation * 5 +
                    ((baseResults.modelRevenue + unlimitedRevenueIncrease * 5) *
                        0.01) /
                        wholesalePrice) *
                    (baseResults.lbsProduced * costToProduce +
                        unlimitedLbsCultivation * 5 * (costToProduce / 5))) /
                    (baseResults.lbsProduced +
                        unlimitedLbsCultivation * 5 +
                        ((baseResults.modelRevenue +
                            unlimitedRevenueIncrease * 5) *
                            0.01) /
                            wholesalePrice) -
                baseResults.profit) +
            ((baseResults.lbsProduced +
                unlimitedLbsCultivation * 6 +
                ((baseResults.modelRevenue + unlimitedRevenueIncrease * 6) *
                    0.01) /
                    wholesalePrice) *
                wholesalePrice -
                ((baseResults.lbsProduced +
                    unlimitedLbsCultivation * 6 +
                    ((baseResults.modelRevenue + unlimitedRevenueIncrease * 6) *
                        0.01) /
                        wholesalePrice) *
                    (baseResults.lbsProduced * costToProduce +
                        unlimitedLbsCultivation * 6 * (costToProduce / 5))) /
                    (baseResults.lbsProduced +
                        unlimitedLbsCultivation * 6 +
                        ((baseResults.modelRevenue +
                            unlimitedRevenueIncrease * 6) *
                            0.01) /
                            wholesalePrice) -
                baseResults.profit) +
            ((baseResults.lbsProduced +
                unlimitedLbsCultivation * 7 +
                ((baseResults.modelRevenue + unlimitedRevenueIncrease * 7) *
                    0.01) /
                    wholesalePrice) *
                wholesalePrice -
                ((baseResults.lbsProduced +
                    unlimitedLbsCultivation * 7 +
                    ((baseResults.modelRevenue + unlimitedRevenueIncrease * 7) *
                        0.01) /
                        wholesalePrice) *
                    (baseResults.lbsProduced * costToProduce +
                        unlimitedLbsCultivation * 7 * (costToProduce / 5))) /
                    (baseResults.lbsProduced +
                        unlimitedLbsCultivation * 7 +
                        ((baseResults.modelRevenue +
                            unlimitedRevenueIncrease * 7) *
                            0.01) /
                            wholesalePrice) -
                baseResults.profit) +
            ((baseResults.lbsProduced +
                unlimitedLbsCultivation * 8 +
                ((baseResults.modelRevenue + unlimitedRevenueIncrease * 8) *
                    0.01) /
                    wholesalePrice) *
                wholesalePrice -
                ((baseResults.lbsProduced +
                    unlimitedLbsCultivation * 8 +
                    ((baseResults.modelRevenue + unlimitedRevenueIncrease * 8) *
                        0.01) /
                        wholesalePrice) *
                    (baseResults.lbsProduced * costToProduce +
                        unlimitedLbsCultivation * 8 * (costToProduce / 5))) /
                    (baseResults.lbsProduced +
                        unlimitedLbsCultivation * 8 +
                        ((baseResults.modelRevenue +
                            unlimitedRevenueIncrease * 8) *
                            0.01) /
                            wholesalePrice) -
                baseResults.profit) +
            ((baseResults.lbsProduced +
                unlimitedLbsCultivation * 9 +
                ((baseResults.modelRevenue + unlimitedRevenueIncrease * 9) *
                    0.01) /
                    wholesalePrice) *
                wholesalePrice -
                ((baseResults.lbsProduced +
                    unlimitedLbsCultivation * 9 +
                    ((baseResults.modelRevenue + unlimitedRevenueIncrease * 9) *
                        0.01) /
                        wholesalePrice) *
                    (baseResults.lbsProduced * costToProduce +
                        unlimitedLbsCultivation * 9 * (costToProduce / 5))) /
                    (baseResults.lbsProduced +
                        unlimitedLbsCultivation * 9 +
                        ((baseResults.modelRevenue +
                            unlimitedRevenueIncrease * 9) *
                            0.01) /
                            wholesalePrice) -
                baseResults.profit) +
            ((baseResults.lbsProduced +
                unlimitedLbsCultivation * 10 +
                ((baseResults.modelRevenue + unlimitedRevenueIncrease * 10) *
                    0.01) /
                    wholesalePrice) *
                wholesalePrice -
                ((baseResults.lbsProduced +
                    unlimitedLbsCultivation * 10 +
                    ((baseResults.modelRevenue +
                        unlimitedRevenueIncrease * 10) *
                        0.01) /
                        wholesalePrice) *
                    (baseResults.lbsProduced * costToProduce +
                        unlimitedLbsCultivation * 10 * (costToProduce / 5))) /
                    (baseResults.lbsProduced +
                        unlimitedLbsCultivation * 10 +
                        ((baseResults.modelRevenue +
                            unlimitedRevenueIncrease * 10) *
                            0.01) /
                            wholesalePrice) -
                baseResults.profit),
        unlimitedYearTwo:
            (baseResults.lbsProduced +
                unlimitedLbsCultivation * 11 +
                ((baseResults.modelRevenue + unlimitedRevenueIncrease * 11) *
                    0.01) /
                    wholesalePrice) *
                wholesalePrice -
            ((baseResults.lbsProduced +
                unlimitedLbsCultivation * 11 +
                ((baseResults.modelRevenue + unlimitedRevenueIncrease * 11) *
                    0.01) /
                    wholesalePrice) *
                (baseResults.lbsProduced * costToProduce +
                    unlimitedLbsCultivation * 11 * (costToProduce / 5))) /
                (baseResults.lbsProduced +
                    unlimitedLbsCultivation * 11 +
                    ((baseResults.modelRevenue +
                        unlimitedRevenueIncrease * 11) *
                        0.01) /
                        wholesalePrice) -
            baseResults.profit +
            ((baseResults.lbsProduced +
                unlimitedLbsCultivation * 12 +
                ((baseResults.modelRevenue + unlimitedRevenueIncrease * 12) *
                    0.01) /
                    wholesalePrice) *
                wholesalePrice -
                ((baseResults.lbsProduced +
                    unlimitedLbsCultivation * 12 +
                    ((baseResults.modelRevenue +
                        unlimitedRevenueIncrease * 12) *
                        0.01) /
                        wholesalePrice) *
                    (baseResults.lbsProduced * costToProduce +
                        unlimitedLbsCultivation * 12 * (costToProduce / 5))) /
                    (baseResults.lbsProduced +
                        unlimitedLbsCultivation * 12 +
                        ((baseResults.modelRevenue +
                            unlimitedRevenueIncrease * 12) *
                            0.01) /
                            wholesalePrice) -
                baseResults.profit) +
            ((baseResults.lbsProduced +
                unlimitedLbsCultivation * 12 +
                ((baseResults.modelRevenue + unlimitedRevenueIncrease * 12) *
                    0.01) /
                    wholesalePrice) *
                wholesalePrice -
                ((baseResults.lbsProduced +
                    unlimitedLbsCultivation * 12 +
                    ((baseResults.modelRevenue +
                        unlimitedRevenueIncrease * 12) *
                        0.01) /
                        wholesalePrice) *
                    (baseResults.lbsProduced * costToProduce +
                        unlimitedLbsCultivation * 12 * (costToProduce / 5))) /
                    (baseResults.lbsProduced +
                        unlimitedLbsCultivation * 12 +
                        ((baseResults.modelRevenue +
                            unlimitedRevenueIncrease * 12) *
                            0.01) /
                            wholesalePrice) -
                baseResults.profit) +
            ((baseResults.lbsProduced +
                unlimitedLbsCultivation * 12 +
                ((baseResults.modelRevenue + unlimitedRevenueIncrease * 12) *
                    0.01) /
                    wholesalePrice) *
                wholesalePrice -
                ((baseResults.lbsProduced +
                    unlimitedLbsCultivation * 12 +
                    ((baseResults.modelRevenue +
                        unlimitedRevenueIncrease * 12) *
                        0.01) /
                        wholesalePrice) *
                    (baseResults.lbsProduced * costToProduce +
                        unlimitedLbsCultivation * 12 * (costToProduce / 5))) /
                    (baseResults.lbsProduced +
                        unlimitedLbsCultivation * 12 +
                        ((baseResults.modelRevenue +
                            unlimitedRevenueIncrease * 12) *
                            0.01) /
                            wholesalePrice) -
                baseResults.profit) +
            ((baseResults.lbsProduced +
                unlimitedLbsCultivation * 12 +
                ((baseResults.modelRevenue + unlimitedRevenueIncrease * 12) *
                    0.01) /
                    wholesalePrice) *
                wholesalePrice -
                ((baseResults.lbsProduced +
                    unlimitedLbsCultivation * 12 +
                    ((baseResults.modelRevenue +
                        unlimitedRevenueIncrease * 12) *
                        0.01) /
                        wholesalePrice) *
                    (baseResults.lbsProduced * costToProduce +
                        unlimitedLbsCultivation * 12 * (costToProduce / 5))) /
                    (baseResults.lbsProduced +
                        unlimitedLbsCultivation * 12 +
                        ((baseResults.modelRevenue +
                            unlimitedRevenueIncrease * 12) *
                            0.01) /
                            wholesalePrice) -
                baseResults.profit) +
            ((baseResults.lbsProduced +
                unlimitedLbsCultivation * 12 +
                ((baseResults.modelRevenue + unlimitedRevenueIncrease * 12) *
                    0.01) /
                    wholesalePrice) *
                wholesalePrice -
                ((baseResults.lbsProduced +
                    unlimitedLbsCultivation * 12 +
                    ((baseResults.modelRevenue +
                        unlimitedRevenueIncrease * 12) *
                        0.01) /
                        wholesalePrice) *
                    (baseResults.lbsProduced * costToProduce +
                        unlimitedLbsCultivation * 12 * (costToProduce / 5))) /
                    (baseResults.lbsProduced +
                        unlimitedLbsCultivation * 12 +
                        ((baseResults.modelRevenue +
                            unlimitedRevenueIncrease * 12) *
                            0.01) /
                            wholesalePrice) -
                baseResults.profit) +
            ((baseResults.lbsProduced +
                unlimitedLbsCultivation * 12 +
                ((baseResults.modelRevenue + unlimitedRevenueIncrease * 12) *
                    0.01) /
                    wholesalePrice) *
                wholesalePrice -
                ((baseResults.lbsProduced +
                    unlimitedLbsCultivation * 12 +
                    ((baseResults.modelRevenue +
                        unlimitedRevenueIncrease * 12) *
                        0.01) /
                        wholesalePrice) *
                    (baseResults.lbsProduced * costToProduce +
                        unlimitedLbsCultivation * 12 * (costToProduce / 5))) /
                    (baseResults.lbsProduced +
                        unlimitedLbsCultivation * 12 +
                        ((baseResults.modelRevenue +
                            unlimitedRevenueIncrease * 12) *
                            0.01) /
                            wholesalePrice) -
                baseResults.profit) +
            ((baseResults.lbsProduced +
                unlimitedLbsCultivation * 12 +
                ((baseResults.modelRevenue + unlimitedRevenueIncrease * 12) *
                    0.01) /
                    wholesalePrice) *
                wholesalePrice -
                ((baseResults.lbsProduced +
                    unlimitedLbsCultivation * 12 +
                    ((baseResults.modelRevenue +
                        unlimitedRevenueIncrease * 12) *
                        0.01) /
                        wholesalePrice) *
                    (baseResults.lbsProduced * costToProduce +
                        unlimitedLbsCultivation * 12 * (costToProduce / 5))) /
                    (baseResults.lbsProduced +
                        unlimitedLbsCultivation * 12 +
                        ((baseResults.modelRevenue +
                            unlimitedRevenueIncrease * 12) *
                            0.01) /
                            wholesalePrice) -
                baseResults.profit) +
            ((baseResults.lbsProduced +
                unlimitedLbsCultivation * 12 +
                ((baseResults.modelRevenue + unlimitedRevenueIncrease * 12) *
                    0.01) /
                    wholesalePrice) *
                wholesalePrice -
                ((baseResults.lbsProduced +
                    unlimitedLbsCultivation * 12 +
                    ((baseResults.modelRevenue +
                        unlimitedRevenueIncrease * 12) *
                        0.01) /
                        wholesalePrice) *
                    (baseResults.lbsProduced * costToProduce +
                        unlimitedLbsCultivation * 12 * (costToProduce / 5))) /
                    (baseResults.lbsProduced +
                        unlimitedLbsCultivation * 12 +
                        ((baseResults.modelRevenue +
                            unlimitedRevenueIncrease * 12) *
                            0.01) /
                            wholesalePrice) -
                baseResults.profit) +
            ((baseResults.lbsProduced +
                unlimitedLbsCultivation * 12 +
                ((baseResults.modelRevenue + unlimitedRevenueIncrease * 12) *
                    0.01) /
                    wholesalePrice) *
                wholesalePrice -
                ((baseResults.lbsProduced +
                    unlimitedLbsCultivation * 12 +
                    ((baseResults.modelRevenue +
                        unlimitedRevenueIncrease * 12) *
                        0.01) /
                        wholesalePrice) *
                    (baseResults.lbsProduced * costToProduce +
                        unlimitedLbsCultivation * 12 * (costToProduce / 5))) /
                    (baseResults.lbsProduced +
                        unlimitedLbsCultivation * 12 +
                        ((baseResults.modelRevenue +
                            unlimitedRevenueIncrease * 12) *
                            0.01) /
                            wholesalePrice) -
                baseResults.profit) +
            ((baseResults.lbsProduced +
                unlimitedLbsCultivation * 12 +
                ((baseResults.modelRevenue + unlimitedRevenueIncrease * 12) *
                    0.01) /
                    wholesalePrice) *
                wholesalePrice -
                ((baseResults.lbsProduced +
                    unlimitedLbsCultivation * 12 +
                    ((baseResults.modelRevenue +
                        unlimitedRevenueIncrease * 12) *
                        0.01) /
                        wholesalePrice) *
                    (baseResults.lbsProduced * costToProduce +
                        unlimitedLbsCultivation * 12 * (costToProduce / 5))) /
                    (baseResults.lbsProduced +
                        unlimitedLbsCultivation * 12 +
                        ((baseResults.modelRevenue +
                            unlimitedRevenueIncrease * 12) *
                            0.01) /
                            wholesalePrice) -
                baseResults.profit) +
            ((baseResults.lbsProduced +
                unlimitedLbsCultivation * 12 +
                ((baseResults.modelRevenue + unlimitedRevenueIncrease * 12) *
                    0.01) /
                    wholesalePrice) *
                wholesalePrice -
                ((baseResults.lbsProduced +
                    unlimitedLbsCultivation * 12 +
                    ((baseResults.modelRevenue +
                        unlimitedRevenueIncrease * 12) *
                        0.01) /
                        wholesalePrice) *
                    (baseResults.lbsProduced * costToProduce +
                        unlimitedLbsCultivation * 12 * (costToProduce / 5))) /
                    (baseResults.lbsProduced +
                        unlimitedLbsCultivation * 12 +
                        ((baseResults.modelRevenue +
                            unlimitedRevenueIncrease * 12) *
                            0.01) /
                            wholesalePrice) -
                baseResults.profit),
        unlimitedYearThree:
            (baseResults.lbsProduced +
                unlimitedLbsCultivation * 12 +
                ((baseResults.modelRevenue + unlimitedRevenueIncrease * 12) *
                    0.01) /
                    wholesalePrice) *
                wholesalePrice -
            ((baseResults.lbsProduced +
                unlimitedLbsCultivation * 12 +
                ((baseResults.modelRevenue + unlimitedRevenueIncrease * 12) *
                    0.01) /
                    wholesalePrice) *
                (baseResults.lbsProduced * costToProduce +
                    unlimitedLbsCultivation * 12 * (costToProduce / 5))) /
                (baseResults.lbsProduced +
                    unlimitedLbsCultivation * 12 +
                    ((baseResults.modelRevenue +
                        unlimitedRevenueIncrease * 12) *
                        0.01) /
                        wholesalePrice) -
            baseResults.profit +
            ((baseResults.lbsProduced +
                unlimitedLbsCultivation * 12 +
                ((baseResults.modelRevenue + unlimitedRevenueIncrease * 12) *
                    0.01) /
                    wholesalePrice) *
                wholesalePrice -
                ((baseResults.lbsProduced +
                    unlimitedLbsCultivation * 12 +
                    ((baseResults.modelRevenue +
                        unlimitedRevenueIncrease * 12) *
                        0.01) /
                        wholesalePrice) *
                    (baseResults.lbsProduced * costToProduce +
                        unlimitedLbsCultivation * 12 * (costToProduce / 5))) /
                    (baseResults.lbsProduced +
                        unlimitedLbsCultivation * 12 +
                        ((baseResults.modelRevenue +
                            unlimitedRevenueIncrease * 12) *
                            0.01) /
                            wholesalePrice) -
                baseResults.profit) +
            ((baseResults.lbsProduced +
                unlimitedLbsCultivation * 12 +
                ((baseResults.modelRevenue + unlimitedRevenueIncrease * 12) *
                    0.01) /
                    wholesalePrice) *
                wholesalePrice -
                ((baseResults.lbsProduced +
                    unlimitedLbsCultivation * 12 +
                    ((baseResults.modelRevenue +
                        unlimitedRevenueIncrease * 12) *
                        0.01) /
                        wholesalePrice) *
                    (baseResults.lbsProduced * costToProduce +
                        unlimitedLbsCultivation * 12 * (costToProduce / 5))) /
                    (baseResults.lbsProduced +
                        unlimitedLbsCultivation * 12 +
                        ((baseResults.modelRevenue +
                            unlimitedRevenueIncrease * 12) *
                            0.01) /
                            wholesalePrice) -
                baseResults.profit) +
            ((baseResults.lbsProduced +
                unlimitedLbsCultivation * 12 +
                ((baseResults.modelRevenue + unlimitedRevenueIncrease * 12) *
                    0.01) /
                    wholesalePrice) *
                wholesalePrice -
                ((baseResults.lbsProduced +
                    unlimitedLbsCultivation * 12 +
                    ((baseResults.modelRevenue +
                        unlimitedRevenueIncrease * 12) *
                        0.01) /
                        wholesalePrice) *
                    (baseResults.lbsProduced * costToProduce +
                        unlimitedLbsCultivation * 12 * (costToProduce / 5))) /
                    (baseResults.lbsProduced +
                        unlimitedLbsCultivation * 12 +
                        ((baseResults.modelRevenue +
                            unlimitedRevenueIncrease * 12) *
                            0.01) /
                            wholesalePrice) -
                baseResults.profit) +
            ((baseResults.lbsProduced +
                unlimitedLbsCultivation * 12 +
                ((baseResults.modelRevenue + unlimitedRevenueIncrease * 12) *
                    0.01) /
                    wholesalePrice) *
                wholesalePrice -
                ((baseResults.lbsProduced +
                    unlimitedLbsCultivation * 12 +
                    ((baseResults.modelRevenue +
                        unlimitedRevenueIncrease * 12) *
                        0.01) /
                        wholesalePrice) *
                    (baseResults.lbsProduced * costToProduce +
                        unlimitedLbsCultivation * 12 * (costToProduce / 5))) /
                    (baseResults.lbsProduced +
                        unlimitedLbsCultivation * 12 +
                        ((baseResults.modelRevenue +
                            unlimitedRevenueIncrease * 12) *
                            0.01) /
                            wholesalePrice) -
                baseResults.profit) +
            ((baseResults.lbsProduced +
                unlimitedLbsCultivation * 12 +
                ((baseResults.modelRevenue + unlimitedRevenueIncrease * 12) *
                    0.01) /
                    wholesalePrice) *
                wholesalePrice -
                ((baseResults.lbsProduced +
                    unlimitedLbsCultivation * 12 +
                    ((baseResults.modelRevenue +
                        unlimitedRevenueIncrease * 12) *
                        0.01) /
                        wholesalePrice) *
                    (baseResults.lbsProduced * costToProduce +
                        unlimitedLbsCultivation * 12 * (costToProduce / 5))) /
                    (baseResults.lbsProduced +
                        unlimitedLbsCultivation * 12 +
                        ((baseResults.modelRevenue +
                            unlimitedRevenueIncrease * 12) *
                            0.01) /
                            wholesalePrice) -
                baseResults.profit) +
            ((baseResults.lbsProduced +
                unlimitedLbsCultivation * 12 +
                ((baseResults.modelRevenue + unlimitedRevenueIncrease * 12) *
                    0.01) /
                    wholesalePrice) *
                wholesalePrice -
                ((baseResults.lbsProduced +
                    unlimitedLbsCultivation * 12 +
                    ((baseResults.modelRevenue +
                        unlimitedRevenueIncrease * 12) *
                        0.01) /
                        wholesalePrice) *
                    (baseResults.lbsProduced * costToProduce +
                        unlimitedLbsCultivation * 12 * (costToProduce / 5))) /
                    (baseResults.lbsProduced +
                        unlimitedLbsCultivation * 12 +
                        ((baseResults.modelRevenue +
                            unlimitedRevenueIncrease * 12) *
                            0.01) /
                            wholesalePrice) -
                baseResults.profit) +
            ((baseResults.lbsProduced +
                unlimitedLbsCultivation * 12 +
                ((baseResults.modelRevenue + unlimitedRevenueIncrease * 12) *
                    0.01) /
                    wholesalePrice) *
                wholesalePrice -
                ((baseResults.lbsProduced +
                    unlimitedLbsCultivation * 12 +
                    ((baseResults.modelRevenue +
                        unlimitedRevenueIncrease * 12) *
                        0.01) /
                        wholesalePrice) *
                    (baseResults.lbsProduced * costToProduce +
                        unlimitedLbsCultivation * 12 * (costToProduce / 5))) /
                    (baseResults.lbsProduced +
                        unlimitedLbsCultivation * 12 +
                        ((baseResults.modelRevenue +
                            unlimitedRevenueIncrease * 12) *
                            0.01) /
                            wholesalePrice) -
                baseResults.profit) +
            ((baseResults.lbsProduced +
                unlimitedLbsCultivation * 12 +
                ((baseResults.modelRevenue + unlimitedRevenueIncrease * 12) *
                    0.01) /
                    wholesalePrice) *
                wholesalePrice -
                ((baseResults.lbsProduced +
                    unlimitedLbsCultivation * 12 +
                    ((baseResults.modelRevenue +
                        unlimitedRevenueIncrease * 12) *
                        0.01) /
                        wholesalePrice) *
                    (baseResults.lbsProduced * costToProduce +
                        unlimitedLbsCultivation * 12 * (costToProduce / 5))) /
                    (baseResults.lbsProduced +
                        unlimitedLbsCultivation * 12 +
                        ((baseResults.modelRevenue +
                            unlimitedRevenueIncrease * 12) *
                            0.01) /
                            wholesalePrice) -
                baseResults.profit) +
            ((baseResults.lbsProduced +
                unlimitedLbsCultivation * 12 +
                ((baseResults.modelRevenue + unlimitedRevenueIncrease * 12) *
                    0.01) /
                    wholesalePrice) *
                wholesalePrice -
                ((baseResults.lbsProduced +
                    unlimitedLbsCultivation * 12 +
                    ((baseResults.modelRevenue +
                        unlimitedRevenueIncrease * 12) *
                        0.01) /
                        wholesalePrice) *
                    (baseResults.lbsProduced * costToProduce +
                        unlimitedLbsCultivation * 12 * (costToProduce / 5))) /
                    (baseResults.lbsProduced +
                        unlimitedLbsCultivation * 12 +
                        ((baseResults.modelRevenue +
                            unlimitedRevenueIncrease * 12) *
                            0.01) /
                            wholesalePrice) -
                baseResults.profit) +
            ((baseResults.lbsProduced +
                unlimitedLbsCultivation * 12 +
                ((baseResults.modelRevenue + unlimitedRevenueIncrease * 12) *
                    0.01) /
                    wholesalePrice) *
                wholesalePrice -
                ((baseResults.lbsProduced +
                    unlimitedLbsCultivation * 12 +
                    ((baseResults.modelRevenue +
                        unlimitedRevenueIncrease * 12) *
                        0.01) /
                        wholesalePrice) *
                    (baseResults.lbsProduced * costToProduce +
                        unlimitedLbsCultivation * 12 * (costToProduce / 5))) /
                    (baseResults.lbsProduced +
                        unlimitedLbsCultivation * 12 +
                        ((baseResults.modelRevenue +
                            unlimitedRevenueIncrease * 12) *
                            0.01) /
                            wholesalePrice) -
                baseResults.profit) +
            ((baseResults.lbsProduced +
                unlimitedLbsCultivation * 12 +
                ((baseResults.modelRevenue + unlimitedRevenueIncrease * 12) *
                    0.01) /
                    wholesalePrice) *
                wholesalePrice -
                ((baseResults.lbsProduced +
                    unlimitedLbsCultivation * 12 +
                    ((baseResults.modelRevenue +
                        unlimitedRevenueIncrease * 12) *
                        0.01) /
                        wholesalePrice) *
                    (baseResults.lbsProduced * costToProduce +
                        unlimitedLbsCultivation * 12 * (costToProduce / 5))) /
                    (baseResults.lbsProduced +
                        unlimitedLbsCultivation * 12 +
                        ((baseResults.modelRevenue +
                            unlimitedRevenueIncrease * 12) *
                            0.01) /
                            wholesalePrice) -
                baseResults.profit),
    };
    const companyRef = useRef(null);
    let navigate = useNavigate();
    const [params] = useSearchParams();
    const unlock = params.get('unlock');
    const pdfDownload = params.get('pdf');
    const plan = params.get('plan');
    const [instance] = usePDF({
        document: (
            <PdfDocPlan
                goRevenueIncrease={goRevenueIncrease}
                proRevenueIncrease={proRevenueIncrease}
                eliteRevenueIncrease={eliteRevenueIncrease}
                unlimitedRevenueIncrease={unlimitedRevenueIncrease}
                goLbsCultivation={goLbsCultivation}
                proLbsCultivation={proLbsCultivation}
                eliteLbsCultivation={eliteLbsCultivation}
                unlimitedLbsCultivation={unlimitedLbsCultivation}
                goSubstrateCount={goSubstrateCount}
                proSubstrateCount={proSubstrateCount}
                eliteSubstrateCount={eliteSubstrateCount}
                unlimitedSubstrateCount={unlimitedSubstrateCount}
                goClimateCount={goClimateCount}
                proClimateCount={proClimateCount}
                eliteClimateCount={eliteClimateCount}
                unlimitedClimateCount={unlimitedClimateCount}
                goRepeaterCount={goRepeaterCount}
                proRepeaterCount={proRepeaterCount}
                eliteRepeaterCount={eliteRepeaterCount}
                unlimitedRepeaterCount={unlimitedRepeaterCount}
                goGatewayCount={goGatewayCount}
                proGatewayCount={proGatewayCount}
                eliteGatewayCount={eliteGatewayCount}
                unlimitedGatewayCount={unlimitedGatewayCount}
                goTotalHardwareCost={goTotalHardwareCost}
                proTotalHardwareCost={proTotalHardwareCost}
                eliteTotalHardwareCost={eliteTotalHardwareCost}
                unlimitedTotalHardwareCost={unlimitedTotalHardwareCost}
                proSoftwareFee={proSoftwareFee}
                eliteSubscriptionCost={eliteSubscriptionCost}
                unlimitedSubscriptionCost={unlimitedSubscriptionCost}
                plan={plan}
                sqFtCanopy={sqFtCanopy}
                gSqFtHarvest={gSqFtHarvest}
                wholesalePrice={wholesalePrice}
                costToProduce={costToProduce}
                harvestsPerYr={harvestsPerYr}
                propRooms={propRooms}
                vegRooms={vegRooms}
                flowerRooms={flowerRooms}
                dryingRooms={dryingRooms}
                baseResults={baseResults}
            />
        ),
    });

    const dataBar = [
        {
            name: 'Year 1',
            swCost: unlimitedSubscriptionCost,
            hwDp: Math.round(unlimitedTotalHardwareCost * 0.2),
            hwLease:
                Math.round(
                    computeMonthLease(
                        unlimitedTotalHardwareCost,
                        Math.round(unlimitedTotalHardwareCost * 0.2)
                    )
                ) * 12,
            profit:
                profits.unlimitedYearOne -
                    unlimitedSubscriptionCost -
                    Math.round(unlimitedTotalHardwareCost * 0.2) -
                    Math.round(
                        computeMonthLease(
                            unlimitedTotalHardwareCost,
                            Math.round(unlimitedTotalHardwareCost * 0.2)
                        )
                    ) *
                        12 <=
                0
                    ? 0
                    : profits.unlimitedYearOne -
                      unlimitedSubscriptionCost -
                      Math.round(unlimitedTotalHardwareCost * 0.2) -
                      Math.round(
                          computeMonthLease(
                              unlimitedTotalHardwareCost,
                              Math.round(unlimitedTotalHardwareCost * 0.2)
                          )
                      ) *
                          12,
            revenue:
                Math.round(unlimitedRevenueIncrease) +
                Math.round(unlimitedRevenueIncrease) * 2 +
                Math.round(unlimitedRevenueIncrease) * 3 +
                Math.round(unlimitedRevenueIncrease) * 4 +
                Math.round(unlimitedRevenueIncrease) * 5 +
                Math.round(unlimitedRevenueIncrease) * 6 +
                Math.round(unlimitedRevenueIncrease) * 7 +
                Math.round(unlimitedRevenueIncrease) * 8 +
                Math.round(unlimitedRevenueIncrease) * 9 +
                Math.round(unlimitedRevenueIncrease) * 10 +
                Math.round(dryingRevenueIncrease) * 11,
        },
        {
            name: 'Year 2',
            swCost: unlimitedSubscriptionCost,
            hwLease:
                Math.round(
                    computeMonthLease(
                        unlimitedTotalHardwareCost,
                        Math.round(unlimitedTotalHardwareCost * 0.2)
                    )
                ) * 12,
            profit:
                profits.unlimitedYearTwo -
                    unlimitedSubscriptionCost -
                    Math.round(
                        computeMonthLease(
                            unlimitedTotalHardwareCost,
                            Math.round(unlimitedTotalHardwareCost * 0.2)
                        )
                    ) *
                        12 <=
                0
                    ? 0
                    : profits.unlimitedYearTwo -
                      unlimitedSubscriptionCost -
                      Math.round(
                          computeMonthLease(
                              unlimitedTotalHardwareCost,
                              Math.round(unlimitedTotalHardwareCost * 0.2)
                          )
                      ) *
                          12,
            revenue:
                Math.round(unlimitedRevenueIncrease) * 11 +
                Math.round(unlimitedRevenueIncrease) * 12 +
                Math.round(unlimitedRevenueIncrease) * 12 +
                Math.round(unlimitedRevenueIncrease) * 12 +
                Math.round(unlimitedRevenueIncrease) * 12 +
                Math.round(unlimitedRevenueIncrease) * 12 +
                Math.round(unlimitedRevenueIncrease) * 12 +
                Math.round(unlimitedRevenueIncrease) * 12 +
                Math.round(unlimitedRevenueIncrease) * 12 +
                Math.round(unlimitedRevenueIncrease) * 12 +
                Math.round(unlimitedRevenueIncrease) * 12 +
                Math.round(unlimitedRevenueIncrease) * 12 +
                Math.round(dryingRevenueIncrease) * 12,
        },
        {
            name: 'Year 3',
            swCost: unlimitedSubscriptionCost,
            hwLease:
                Math.round(
                    computeMonthLease(
                        unlimitedTotalHardwareCost,
                        Math.round(unlimitedTotalHardwareCost * 0.2)
                    )
                ) * 12,
            profit:
                profits.unlimitedYearThree -
                    unlimitedSubscriptionCost -
                    Math.round(
                        computeMonthLease(
                            unlimitedTotalHardwareCost,
                            Math.round(unlimitedTotalHardwareCost * 0.2)
                        )
                    ) *
                        12 <=
                0
                    ? 0
                    : profits.unlimitedYearThree -
                      unlimitedSubscriptionCost -
                      Math.round(
                          computeMonthLease(
                              unlimitedTotalHardwareCost,
                              Math.round(unlimitedTotalHardwareCost * 0.2)
                          )
                      ) *
                          12,
            revenue:
                Math.round(unlimitedRevenueIncrease) * 12 +
                Math.round(unlimitedRevenueIncrease) * 12 +
                Math.round(unlimitedRevenueIncrease) * 12 +
                Math.round(unlimitedRevenueIncrease) * 12 +
                Math.round(unlimitedRevenueIncrease) * 12 +
                Math.round(unlimitedRevenueIncrease) * 12 +
                Math.round(unlimitedRevenueIncrease) * 12 +
                Math.round(unlimitedRevenueIncrease) * 12 +
                Math.round(unlimitedRevenueIncrease) * 12 +
                Math.round(unlimitedRevenueIncrease) * 12 +
                Math.round(unlimitedRevenueIncrease) * 12 +
                Math.round(unlimitedRevenueIncrease) * 12 +
                Math.round(dryingRevenueIncrease) * 12,
        },
    ];
    const dataBarElite = [
        {
            name: 'Year 1',
            swCost: eliteSubscriptionCost,
            hwDp: Math.round(eliteTotalHardwareCost * 0.2),
            hwLease:
                Math.round(
                    computeMonthLease(
                        eliteTotalHardwareCost,
                        Math.round(eliteTotalHardwareCost * 0.2)
                    )
                ) * 12,
            profit:
                profits.eliteYearOne -
                    eliteSubscriptionCost -
                    Math.round(eliteTotalHardwareCost * 0.2) -
                    Math.round(
                        computeMonthLease(
                            eliteTotalHardwareCost,
                            Math.round(eliteTotalHardwareCost * 0.2)
                        )
                    ) *
                        12 <=
                0
                    ? 0
                    : profits.eliteYearOne -
                      eliteSubscriptionCost -
                      Math.round(eliteTotalHardwareCost * 0.2) -
                      Math.round(
                          computeMonthLease(
                              eliteTotalHardwareCost,
                              Math.round(eliteTotalHardwareCost * 0.2)
                          )
                      ) *
                          12,
            revenue:
                Math.round(eliteRevenueIncrease) +
                Math.round(eliteRevenueIncrease) * 2 +
                Math.round(eliteRevenueIncrease) * 3 +
                Math.round(eliteRevenueIncrease) * 4 +
                Math.round(eliteRevenueIncrease) * 5 +
                Math.round(eliteRevenueIncrease) * 6 +
                Math.round(eliteRevenueIncrease) * 7 +
                Math.round(eliteRevenueIncrease) * 8 +
                Math.round(eliteRevenueIncrease) * 9 +
                Math.round(dryingRevenueIncrease) * 11,
        },
        {
            name: 'Year 2',
            swCost: eliteSubscriptionCost,
            hwLease:
                Math.round(
                    computeMonthLease(
                        eliteTotalHardwareCost,
                        Math.round(eliteTotalHardwareCost * 0.2)
                    )
                ) * 12,
            profit:
                profits.eliteYearTwo -
                    eliteSubscriptionCost -
                    Math.round(
                        computeMonthLease(
                            eliteTotalHardwareCost,
                            Math.round(eliteTotalHardwareCost * 0.2)
                        )
                    ) *
                        12 <=
                0
                    ? 0
                    : profits.eliteYearTwo -
                      eliteSubscriptionCost -
                      Math.round(
                          computeMonthLease(
                              eliteTotalHardwareCost,
                              Math.round(eliteTotalHardwareCost * 0.2)
                          )
                      ) *
                          12,
            revenue:
                Math.round(eliteRevenueIncrease) * 10 +
                Math.round(eliteRevenueIncrease) * 11 +
                Math.round(eliteRevenueIncrease) * 12 +
                Math.round(eliteRevenueIncrease) * 12 +
                Math.round(eliteRevenueIncrease) * 12 +
                Math.round(eliteRevenueIncrease) * 12 +
                Math.round(eliteRevenueIncrease) * 12 +
                Math.round(eliteRevenueIncrease) * 12 +
                Math.round(eliteRevenueIncrease) * 12 +
                Math.round(eliteRevenueIncrease) * 12 +
                Math.round(eliteRevenueIncrease) * 12 +
                Math.round(eliteRevenueIncrease) * 12 +
                Math.round(dryingRevenueIncrease) * 12,
        },
        {
            name: 'Year 3',
            swCost: eliteSubscriptionCost,
            hwLease:
                Math.round(
                    computeMonthLease(
                        eliteTotalHardwareCost,
                        Math.round(eliteTotalHardwareCost * 0.2)
                    )
                ) * 12,
            profit:
                profits.eliteYearThree -
                    eliteSubscriptionCost -
                    Math.round(
                        computeMonthLease(
                            eliteTotalHardwareCost,
                            Math.round(eliteTotalHardwareCost * 0.2)
                        )
                    ) *
                        12 <=
                0
                    ? 0
                    : profits.eliteYearThree -
                      eliteSubscriptionCost -
                      Math.round(
                          computeMonthLease(
                              eliteTotalHardwareCost,
                              Math.round(eliteTotalHardwareCost * 0.2)
                          )
                      ) *
                          12,
            revenue:
                Math.round(eliteRevenueIncrease) * 12 +
                Math.round(eliteRevenueIncrease) * 12 +
                Math.round(eliteRevenueIncrease) * 12 +
                Math.round(eliteRevenueIncrease) * 12 +
                Math.round(eliteRevenueIncrease) * 12 +
                Math.round(eliteRevenueIncrease) * 12 +
                Math.round(eliteRevenueIncrease) * 12 +
                Math.round(eliteRevenueIncrease) * 12 +
                Math.round(eliteRevenueIncrease) * 12 +
                Math.round(eliteRevenueIncrease) * 12 +
                Math.round(eliteRevenueIncrease) * 12 +
                Math.round(eliteRevenueIncrease) * 12 +
                Math.round(dryingRevenueIncrease) * 12,
        },
    ];
    const dataBarPro = [
        {
            name: 'Year 1',
            swCost: proSoftwareFee,
            hwDp: Math.round(proTotalHardwareCost * 0.2),
            hwLease:
                Math.round(
                    computeMonthLease(
                        proTotalHardwareCost,
                        Math.round(proTotalHardwareCost * 0.2)
                    )
                ) * 12,
            profit:
                profits.proYearOne -
                    proSoftwareFee -
                    Math.round(proTotalHardwareCost * 0.2) -
                    Math.round(
                        computeMonthLease(
                            proTotalHardwareCost,
                            Math.round(proTotalHardwareCost * 0.2)
                        )
                    ) *
                        12 <=
                0
                    ? 0
                    : profits.proYearOne -
                      proSoftwareFee -
                      Math.round(proTotalHardwareCost * 0.2) -
                      Math.round(
                          computeMonthLease(
                              proTotalHardwareCost,
                              Math.round(proTotalHardwareCost * 0.2)
                          )
                      ) *
                          12,
            revenue:
                Math.round(proRevenueIncrease) +
                Math.round(proRevenueIncrease) * 2 +
                Math.round(proRevenueIncrease) * 3 +
                Math.round(proRevenueIncrease) * 4 +
                Math.round(proRevenueIncrease) * 5 +
                Math.round(proRevenueIncrease) * 6 +
                Math.round(proRevenueIncrease) * 7 +
                Math.round(proRevenueIncrease) * 8 +
                Math.round(dryingRevenueIncrease) * 11,
        },
        {
            name: 'Year 2',
            swCost: proSoftwareFee,
            hwLease:
                Math.round(
                    computeMonthLease(
                        proTotalHardwareCost,
                        Math.round(proTotalHardwareCost * 0.2)
                    )
                ) * 12,
            profit:
                profits.proYearTwo -
                    proSoftwareFee -
                    Math.round(
                        computeMonthLease(
                            proTotalHardwareCost,
                            Math.round(proTotalHardwareCost * 0.2)
                        )
                    ) *
                        12 <=
                0
                    ? 0
                    : profits.proYearTwo -
                      proSoftwareFee -
                      Math.round(
                          computeMonthLease(
                              proTotalHardwareCost,
                              Math.round(proTotalHardwareCost * 0.2)
                          )
                      ) *
                          12,
            revenue:
                Math.round(proRevenueIncrease) * 9 +
                Math.round(proRevenueIncrease) * 10 +
                Math.round(proRevenueIncrease) * 11 +
                Math.round(proRevenueIncrease) * 12 +
                Math.round(proRevenueIncrease) * 12 +
                Math.round(proRevenueIncrease) * 12 +
                Math.round(proRevenueIncrease) * 12 +
                Math.round(proRevenueIncrease) * 12 +
                Math.round(proRevenueIncrease) * 12 +
                Math.round(proRevenueIncrease) * 12 +
                Math.round(proRevenueIncrease) * 12 +
                Math.round(proRevenueIncrease) * 12 +
                Math.round(dryingRevenueIncrease) * 12,
        },
        {
            name: 'Year 3',
            swCost: proSoftwareFee,
            hwLease:
                Math.round(
                    computeMonthLease(
                        proTotalHardwareCost,
                        Math.round(proTotalHardwareCost * 0.2)
                    )
                ) * 12,
            profit:
                profits.proYearThree -
                    proSoftwareFee -
                    Math.round(
                        computeMonthLease(
                            proTotalHardwareCost,
                            Math.round(proTotalHardwareCost * 0.2)
                        )
                    ) *
                        12 <=
                0
                    ? 0
                    : profits.proYearThree -
                      proSoftwareFee -
                      Math.round(
                          computeMonthLease(
                              proTotalHardwareCost,
                              Math.round(proTotalHardwareCost * 0.2)
                          )
                      ) *
                          12,
            revenue:
                Math.round(proRevenueIncrease) * 12 +
                Math.round(proRevenueIncrease) * 12 +
                Math.round(proRevenueIncrease) * 12 +
                Math.round(proRevenueIncrease) * 12 +
                Math.round(proRevenueIncrease) * 12 +
                Math.round(proRevenueIncrease) * 12 +
                Math.round(proRevenueIncrease) * 12 +
                Math.round(proRevenueIncrease) * 12 +
                Math.round(proRevenueIncrease) * 12 +
                Math.round(proRevenueIncrease) * 12 +
                Math.round(proRevenueIncrease) * 12 +
                Math.round(proRevenueIncrease) * 12 +
                Math.round(dryingRevenueIncrease) * 12,
        },
    ];
    const dataBarGo = [
        {
            name: 'Year 1',
            swCost: 0,
            hwDp: 0,
            hwLease: 0,
            profit: profits.goYearOne <= 0 ? 0 : profits.goYearOne,
            revenue:
                Math.round(goRevenueIncrease) +
                Math.round(goRevenueIncrease) * 2 +
                Math.round(goRevenueIncrease) * 3 +
                Math.round(goRevenueIncrease) * 4 +
                Math.round(goRevenueIncrease) * 5 +
                Math.round(goRevenueIncrease) * 6 +
                Math.round(goRevenueIncrease) * 7 +
                Math.round(dryingRevenueIncrease) * 11,
        },
        {
            name: 'Year 2',
            swCost: 0,
            hwLease: 0,
            profit: profits.goYearTwo <= 0 ? 0 : profits.goYearTwo,
            revenue:
                Math.round(goRevenueIncrease) * 8 +
                Math.round(goRevenueIncrease) * 9 +
                Math.round(goRevenueIncrease) * 10 +
                Math.round(goRevenueIncrease) * 11 +
                Math.round(goRevenueIncrease) * 12 +
                Math.round(goRevenueIncrease) * 12 +
                Math.round(goRevenueIncrease) * 12 +
                Math.round(goRevenueIncrease) * 12 +
                Math.round(goRevenueIncrease) * 12 +
                Math.round(goRevenueIncrease) * 12 +
                Math.round(goRevenueIncrease) * 12 +
                Math.round(goRevenueIncrease) * 12 +
                Math.round(dryingRevenueIncrease) * 12,
        },
        {
            name: 'Year 3',
            swCost: 0,
            hwLease: 0,
            profit: profits.goYearThree <= 0 ? 0 : profits.goYearThree,
            revenue:
                Math.round(goRevenueIncrease) * 12 +
                Math.round(goRevenueIncrease) * 12 +
                Math.round(goRevenueIncrease) * 12 +
                Math.round(goRevenueIncrease) * 12 +
                Math.round(goRevenueIncrease) * 12 +
                Math.round(goRevenueIncrease) * 12 +
                Math.round(goRevenueIncrease) * 12 +
                Math.round(goRevenueIncrease) * 12 +
                Math.round(goRevenueIncrease) * 12 +
                Math.round(goRevenueIncrease) * 12 +
                Math.round(goRevenueIncrease) * 12 +
                Math.round(goRevenueIncrease) * 12 +
                Math.round(dryingRevenueIncrease) * 12,
        },
    ];

    const formatter = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
    });

    const isDesktop = useMediaQuery({
        query: '(min-width: 800px)',
    });

    function computeMonthLease(totalCost, dpCost) {
        const pv = totalCost - dpCost;
        const rate = 0.08 / 12;
        const nper = 4 * 12;
        return (pv * rate) / (1 - (1 + rate) ** -nper);
    }

    useEffect(() => {
        if (companyRef && window.navigator.onLine && unlock) {
            postscribe(
                companyRef.current,
                `<script type="text/javascript" src="https://go.meter.group/dcjs/304411/1442/dc.js"></script>`
            );
        } else {
            navigate('/');
        }
    }, [navigate, unlock]);

    useEffect(() => {
        if (pdfDownload && instance.url) {
            let a = document.createElement('a');
            a.setAttribute('download', `AROYA ${plan} Plan.pdf`);
            a.setAttribute('href', instance.url);
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
        }
    }, [pdfDownload, instance.url, plan]);

    return (
        <div className={classes.root}>
            <header className={classes.header}>
                <div className={classes.headerDemo}>
                    <DemoModal />
                </div>
                <div className={classes.headerContent}>
                    <Typography
                        variant="h4"
                        component="h1"
                        className={classes.headerTitle}
                    >
                        Your ROI
                    </Typography>
                    <div className={classes.headerContentRight}>
                        <Typography
                            variant="h4"
                            className={classes.headerCollab}
                        >
                            <span id="companyRef" ref={companyRef}></span> x
                            AROYA
                        </Typography>
                    </div>
                </div>
            </header>
            <main>
                <section className={classes.detailsContainer}>
                    <Grid container spacing={6}>
                        <Grid item lg={4} md={5} xs={12}>
                            <div className={classes.detailsInput}>
                                <Typography
                                    variant="h4"
                                    className={classes.detailsTitle}
                                >
                                    AROYA {plan}
                                </Typography>
                                <Typography
                                    variant="body1"
                                    className={classes.detailsText}
                                >
                                    With this data we can calculate the correct
                                    sensor density and how high of a yield
                                    increase you can expect with AROYA within
                                    the first few months
                                </Typography>
                                <TableContainer
                                    className={classes.tableContainer}
                                >
                                    <Table sx={{ minWidth: 650 }} size="small">
                                        <TableBody>
                                            <TableRow>
                                                <TableCell
                                                    component="th"
                                                    scope="row"
                                                    className={clsx(
                                                        classes.tableRowFirst,
                                                        classes.tableHasBorder
                                                    )}
                                                >
                                                    Square feet of canopy
                                                </TableCell>
                                                <TableCell
                                                    align="left"
                                                    className={clsx(
                                                        classes.tableRowLast,
                                                        classes.tableInputContent,
                                                        classes.tableHasBorder
                                                    )}
                                                >
                                                    {sqFtCanopy}
                                                </TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell
                                                    component="th"
                                                    scope="row"
                                                    className={clsx(
                                                        classes.tableRowFirst,
                                                        classes.tableHasBorder
                                                    )}
                                                >
                                                    Current g / sq ft / harvest
                                                </TableCell>
                                                <TableCell
                                                    align="left"
                                                    className={clsx(
                                                        classes.tableRowLast,
                                                        classes.tableInputContent,
                                                        classes.tableHasBorder
                                                    )}
                                                >
                                                    {gSqFtHarvest}
                                                </TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell
                                                    component="th"
                                                    scope="row"
                                                    className={clsx(
                                                        classes.tableRowFirst,
                                                        classes.tableHasBorder
                                                    )}
                                                >
                                                    State wholesale price
                                                </TableCell>
                                                <TableCell
                                                    align="left"
                                                    className={clsx(
                                                        classes.tableRowLast,
                                                        classes.tableInputContent,
                                                        classes.tableHasBorder
                                                    )}
                                                >
                                                    {formatter.format(
                                                        wholesalePrice
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell
                                                    component="th"
                                                    scope="row"
                                                    className={clsx(
                                                        classes.tableRowFirst,
                                                        classes.tableHasBorder
                                                    )}
                                                >
                                                    Cost to produce (lb)
                                                </TableCell>
                                                <TableCell
                                                    align="left"
                                                    className={clsx(
                                                        classes.tableRowLast,
                                                        classes.tableInputContent,
                                                        classes.tableHasBorder
                                                    )}
                                                >
                                                    {formatter.format(
                                                        costToProduce
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell
                                                    component="th"
                                                    scope="row"
                                                    className={clsx(
                                                        classes.tableRowFirst,
                                                        classes.tableHasBorder
                                                    )}
                                                >
                                                    Harvests per year
                                                </TableCell>
                                                <TableCell
                                                    align="left"
                                                    className={clsx(
                                                        classes.tableRowLast,
                                                        classes.tableInputContent,
                                                        classes.tableHasBorder
                                                    )}
                                                >
                                                    {harvestsPerYr}
                                                </TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell
                                                    component="th"
                                                    scope="row"
                                                    className={clsx(
                                                        classes.tableRowFirst,
                                                        classes.tableHasBorder
                                                    )}
                                                >
                                                    Propagation rooms
                                                </TableCell>
                                                <TableCell
                                                    align="left"
                                                    className={clsx(
                                                        classes.tableRowLast,
                                                        classes.tableInputContent,
                                                        classes.tableHasBorder
                                                    )}
                                                >
                                                    {propRooms}
                                                </TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell
                                                    component="th"
                                                    scope="row"
                                                    className={clsx(
                                                        classes.tableRowFirst,
                                                        classes.tableHasBorder
                                                    )}
                                                >
                                                    Veg rooms
                                                </TableCell>
                                                <TableCell
                                                    align="left"
                                                    className={clsx(
                                                        classes.tableRowLast,
                                                        classes.tableInputContent,
                                                        classes.tableHasBorder
                                                    )}
                                                >
                                                    {vegRooms}
                                                </TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell
                                                    component="th"
                                                    scope="row"
                                                    className={clsx(
                                                        classes.tableRowFirst,
                                                        classes.tableHasBorder
                                                    )}
                                                >
                                                    Flower rooms
                                                </TableCell>
                                                <TableCell
                                                    align="left"
                                                    className={clsx(
                                                        classes.tableRowLast,
                                                        classes.tableInputContent,
                                                        classes.tableHasBorder
                                                    )}
                                                >
                                                    {flowerRooms}
                                                </TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell
                                                    component="th"
                                                    scope="row"
                                                    className={clsx(
                                                        classes.tableRowFirst,
                                                        classes.tableHasNoBorder
                                                    )}
                                                >
                                                    Drying rooms
                                                </TableCell>
                                                <TableCell
                                                    align="left"
                                                    className={clsx(
                                                        classes.tableRowLast,
                                                        classes.tableInputContent,
                                                        classes.tableHasNoBorder
                                                    )}
                                                >
                                                    {dryingRooms}
                                                </TableCell>
                                            </TableRow>
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </div>
                        </Grid>
                        <Grid
                            item
                            lg={8}
                            md={7}
                            xs={12}
                            className={classes.detailsRight}
                        >
                            <div className={classes.detailsDownload}>
                                <Button
                                    variant="contained"
                                    color="secondary"
                                    className={classes.downloadButton}
                                >
                                    <PDFDownloadLink
                                        document={
                                            <PdfDocPlan
                                                goRevenueIncrease={
                                                    goRevenueIncrease
                                                }
                                                proRevenueIncrease={
                                                    proRevenueIncrease
                                                }
                                                eliteRevenueIncrease={
                                                    eliteRevenueIncrease
                                                }
                                                unlimitedRevenueIncrease={
                                                    unlimitedRevenueIncrease
                                                }
                                                goLbsCultivation={
                                                    goLbsCultivation
                                                }
                                                proLbsCultivation={
                                                    proLbsCultivation
                                                }
                                                eliteLbsCultivation={
                                                    eliteLbsCultivation
                                                }
                                                unlimitedLbsCultivation={
                                                    unlimitedLbsCultivation
                                                }
                                                goSubstrateCount={
                                                    goSubstrateCount
                                                }
                                                proSubstrateCount={
                                                    proSubstrateCount
                                                }
                                                eliteSubstrateCount={
                                                    eliteSubstrateCount
                                                }
                                                unlimitedSubstrateCount={
                                                    unlimitedSubstrateCount
                                                }
                                                goClimateCount={goClimateCount}
                                                proClimateCount={
                                                    proClimateCount
                                                }
                                                eliteClimateCount={
                                                    eliteClimateCount
                                                }
                                                unlimitedClimateCount={
                                                    unlimitedClimateCount
                                                }
                                                goRepeaterCount={
                                                    goRepeaterCount
                                                }
                                                proRepeaterCount={
                                                    proRepeaterCount
                                                }
                                                eliteRepeaterCount={
                                                    eliteRepeaterCount
                                                }
                                                unlimitedRepeaterCount={
                                                    unlimitedRepeaterCount
                                                }
                                                goGatewayCount={goGatewayCount}
                                                proGatewayCount={
                                                    proGatewayCount
                                                }
                                                eliteGatewayCount={
                                                    eliteGatewayCount
                                                }
                                                unlimitedGatewayCount={
                                                    unlimitedGatewayCount
                                                }
                                                goTotalHardwareCost={
                                                    goTotalHardwareCost
                                                }
                                                proTotalHardwareCost={
                                                    proTotalHardwareCost
                                                }
                                                eliteTotalHardwareCost={
                                                    eliteTotalHardwareCost
                                                }
                                                unlimitedTotalHardwareCost={
                                                    unlimitedTotalHardwareCost
                                                }
                                                proSoftwareFee={proSoftwareFee}
                                                eliteSubscriptionCost={
                                                    eliteSubscriptionCost
                                                }
                                                unlimitedSubscriptionCost={
                                                    unlimitedSubscriptionCost
                                                }
                                                plan={plan}
                                                sqFtCanopy={sqFtCanopy}
                                                gSqFtHarvest={gSqFtHarvest}
                                                wholesalePrice={wholesalePrice}
                                                costToProduce={costToProduce}
                                                harvestsPerYr={harvestsPerYr}
                                                propRooms={propRooms}
                                                vegRooms={vegRooms}
                                                flowerRooms={flowerRooms}
                                                dryingRooms={dryingRooms}
                                                baseResults={baseResults}
                                            />
                                        }
                                        fileName={`AROYA ${plan} Plan.pdf`}
                                    >
                                        {({ blob, url, loading, error }) =>
                                            loading
                                                ? 'Loading...'
                                                : 'Download Full Report'
                                        }
                                    </PDFDownloadLink>
                                </Button>
                            </div>
                            <TableContainer className={classes.tableContainer}>
                                <Table sx={{ minWidth: 650 }}>
                                    <TableBody>
                                        <TableRow>
                                            <TableCell
                                                component="th"
                                                scope="row"
                                                className={clsx(
                                                    classes.tableRowFirst,
                                                    classes.tableHasBorder,
                                                    classes.tableOutputTitle,
                                                    classes.tableHasBorderTop
                                                )}
                                            >
                                                Yield increases start in
                                            </TableCell>
                                            <TableCell
                                                align="left"
                                                className={clsx(
                                                    classes.tableRowLast,
                                                    classes.tableHasBorder,
                                                    classes.tableOutputContent,
                                                    classes.tableHasBorderTop
                                                )}
                                            >
                                                {plan === 'GO' && '6 months'}
                                                {plan === 'PRO' && '5 months'}
                                                {plan === 'Elite' && '4 months'}
                                                {plan === 'Unlimited' &&
                                                    '3 months'}
                                            </TableCell>
                                        </TableRow>
                                        {/* <TableRow>
                                            <TableCell
                                                component="th"
                                                scope="row"
                                                className={clsx(
                                                    classes.tableRowFirst,
                                                    classes.tableHasBorder,
                                                    classes.tableOutputTitle
                                                )}
                                            >
                                                Yield increase each month
                                            </TableCell>
                                            <TableCell
                                                align="left"
                                                className={clsx(
                                                    classes.tableRowLast,
                                                    classes.tableHasBorder,
                                                    classes.tableOutputContent
                                                )}
                                            >
                                                {plan === 'GO' &&
                                                    `${goLbsCultivation} lbs`}
                                                {plan === 'PRO' &&
                                                    `${proLbsCultivation} lbs`}
                                                {plan === 'Elite' &&
                                                    `${eliteLbsCultivation} lbs`}
                                                {plan === 'Unlimited' &&
                                                    `${unlimitedLbsCultivation} lbs`}
                                            </TableCell>
                                        </TableRow> */}
                                        <TableRow>
                                            <TableCell
                                                component="th"
                                                scope="row"
                                                className={clsx(
                                                    classes.tableRowFirst,
                                                    classes.tableHasBorder,
                                                    classes.tableOutputTitle
                                                )}
                                            >
                                                Profit increase year 1
                                            </TableCell>
                                            <TableCell
                                                align="left"
                                                className={clsx(
                                                    classes.tableRowLast,
                                                    classes.tableHasBorder,
                                                    classes.tableOutputContent,
                                                    plan === 'GO' &&
                                                        classes.tableOutputContentMed
                                                )}
                                            >
                                                {plan === 'GO' &&
                                                    `Contact Sales`}
                                                {plan === 'PRO' &&
                                                    `${formatter.format(
                                                        Math.round(
                                                            profits.proYearOne -
                                                                proSoftwareFee -
                                                                Math.round(
                                                                    proTotalHardwareCost *
                                                                        0.2
                                                                ) -
                                                                Math.round(
                                                                    computeMonthLease(
                                                                        proTotalHardwareCost,
                                                                        Math.round(
                                                                            proTotalHardwareCost *
                                                                                0.2
                                                                        )
                                                                    )
                                                                ) *
                                                                    12 <=
                                                                0
                                                                ? 0
                                                                : profits.proYearOne -
                                                                      proSoftwareFee -
                                                                      Math.round(
                                                                          proTotalHardwareCost *
                                                                              0.2
                                                                      ) -
                                                                      Math.round(
                                                                          computeMonthLease(
                                                                              proTotalHardwareCost,
                                                                              Math.round(
                                                                                  proTotalHardwareCost *
                                                                                      0.2
                                                                              )
                                                                          )
                                                                      ) *
                                                                          12
                                                        )
                                                    )}`}
                                                {plan === 'Elite' &&
                                                    `${formatter.format(
                                                        Math.round(
                                                            profits.eliteYearOne -
                                                                eliteSubscriptionCost -
                                                                Math.round(
                                                                    eliteTotalHardwareCost *
                                                                        0.2
                                                                ) -
                                                                Math.round(
                                                                    computeMonthLease(
                                                                        eliteTotalHardwareCost,
                                                                        Math.round(
                                                                            eliteTotalHardwareCost *
                                                                                0.2
                                                                        )
                                                                    )
                                                                ) *
                                                                    12 <=
                                                                0
                                                                ? 0
                                                                : profits.eliteYearOne -
                                                                      eliteSubscriptionCost -
                                                                      Math.round(
                                                                          eliteTotalHardwareCost *
                                                                              0.2
                                                                      ) -
                                                                      Math.round(
                                                                          computeMonthLease(
                                                                              eliteTotalHardwareCost,
                                                                              Math.round(
                                                                                  eliteTotalHardwareCost *
                                                                                      0.2
                                                                              )
                                                                          )
                                                                      ) *
                                                                          12
                                                        )
                                                    )}`}
                                                {plan === 'Unlimited' &&
                                                    `${formatter.format(
                                                        Math.round(
                                                            profits.unlimitedYearOne -
                                                                unlimitedSubscriptionCost -
                                                                Math.round(
                                                                    unlimitedTotalHardwareCost *
                                                                        0.2
                                                                ) -
                                                                Math.round(
                                                                    computeMonthLease(
                                                                        unlimitedTotalHardwareCost,
                                                                        Math.round(
                                                                            unlimitedTotalHardwareCost *
                                                                                0.2
                                                                        )
                                                                    )
                                                                ) *
                                                                    12 <=
                                                                0
                                                                ? 0
                                                                : profits.unlimitedYearOne -
                                                                      unlimitedSubscriptionCost -
                                                                      Math.round(
                                                                          unlimitedTotalHardwareCost *
                                                                              0.2
                                                                      ) -
                                                                      Math.round(
                                                                          computeMonthLease(
                                                                              unlimitedTotalHardwareCost,
                                                                              Math.round(
                                                                                  unlimitedTotalHardwareCost *
                                                                                      0.2
                                                                              )
                                                                          )
                                                                      ) *
                                                                          12
                                                        )
                                                    )}`}
                                            </TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell
                                                component="th"
                                                scope="row"
                                                className={clsx(
                                                    classes.tableRowFirst,
                                                    classes.tableHasBorder,
                                                    classes.tableOutputTitle
                                                )}
                                            >
                                                Profit increase year 2
                                            </TableCell>
                                            <TableCell
                                                align="left"
                                                className={clsx(
                                                    classes.tableRowLast,
                                                    classes.tableHasBorder,
                                                    classes.tableOutputContent,
                                                    plan === 'GO' &&
                                                        classes.tableOutputContentMed
                                                )}
                                            >
                                                {plan === 'GO' &&
                                                    `Contact Sales`}
                                                {plan === 'PRO' &&
                                                    `${formatter.format(
                                                        Math.round(
                                                            profits.proYearTwo -
                                                                proSoftwareFee -
                                                                Math.round(
                                                                    computeMonthLease(
                                                                        proTotalHardwareCost,
                                                                        Math.round(
                                                                            proTotalHardwareCost *
                                                                                0.2
                                                                        )
                                                                    )
                                                                ) *
                                                                    12 <=
                                                                0
                                                                ? 0
                                                                : profits.proYearTwo -
                                                                      proSoftwareFee -
                                                                      Math.round(
                                                                          computeMonthLease(
                                                                              proTotalHardwareCost,
                                                                              Math.round(
                                                                                  proTotalHardwareCost *
                                                                                      0.2
                                                                              )
                                                                          )
                                                                      ) *
                                                                          12
                                                        )
                                                    )}`}
                                                {plan === 'Elite' &&
                                                    `${formatter.format(
                                                        Math.round(
                                                            profits.eliteYearTwo -
                                                                eliteSubscriptionCost -
                                                                Math.round(
                                                                    computeMonthLease(
                                                                        eliteTotalHardwareCost,
                                                                        Math.round(
                                                                            eliteTotalHardwareCost *
                                                                                0.2
                                                                        )
                                                                    )
                                                                ) *
                                                                    12 <=
                                                                0
                                                                ? 0
                                                                : profits.eliteYearTwo -
                                                                      eliteSubscriptionCost -
                                                                      Math.round(
                                                                          computeMonthLease(
                                                                              eliteTotalHardwareCost,
                                                                              Math.round(
                                                                                  eliteTotalHardwareCost *
                                                                                      0.2
                                                                              )
                                                                          )
                                                                      ) *
                                                                          12
                                                        )
                                                    )}`}
                                                {plan === 'Unlimited' &&
                                                    `${formatter.format(
                                                        Math.round(
                                                            profits.unlimitedYearTwo -
                                                                unlimitedSubscriptionCost -
                                                                Math.round(
                                                                    computeMonthLease(
                                                                        unlimitedTotalHardwareCost,
                                                                        Math.round(
                                                                            unlimitedTotalHardwareCost *
                                                                                0.2
                                                                        )
                                                                    )
                                                                ) *
                                                                    12 <=
                                                                0
                                                                ? 0
                                                                : profits.unlimitedYearTwo -
                                                                      unlimitedSubscriptionCost -
                                                                      Math.round(
                                                                          computeMonthLease(
                                                                              unlimitedTotalHardwareCost,
                                                                              Math.round(
                                                                                  unlimitedTotalHardwareCost *
                                                                                      0.2
                                                                              )
                                                                          )
                                                                      ) *
                                                                          12
                                                        )
                                                    )}`}
                                            </TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell
                                                component="th"
                                                scope="row"
                                                className={clsx(
                                                    classes.tableRowFirst,
                                                    classes.tableHasBorder,
                                                    classes.tableOutputTitle
                                                )}
                                            >
                                                Year 1 down payment
                                            </TableCell>
                                            <TableCell
                                                align="left"
                                                className={clsx(
                                                    classes.tableRowLast,
                                                    classes.tableHasBorder,
                                                    classes.tableOutputContent,
                                                    plan === 'GO' &&
                                                        classes.tableOutputContentMed
                                                )}
                                            >
                                                {plan === 'GO' &&
                                                    `Call for pricing`}
                                                {plan === 'PRO' &&
                                                    `${formatter.format(
                                                        Math.round(
                                                            proTotalHardwareCost *
                                                                0.2
                                                        )
                                                    )}`}
                                                {plan === 'Elite' &&
                                                    `${formatter.format(
                                                        Math.round(
                                                            eliteTotalHardwareCost *
                                                                0.2
                                                        )
                                                    )}`}
                                                {plan === 'Unlimited' &&
                                                    `${formatter.format(
                                                        Math.round(
                                                            unlimitedTotalHardwareCost *
                                                                0.2
                                                        )
                                                    )}`}
                                            </TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell
                                                component="th"
                                                scope="row"
                                                className={clsx(
                                                    classes.tableRowFirst,
                                                    classes.tableHasBorder,
                                                    classes.tableOutputTitle
                                                )}
                                            >
                                                Monthly hardware lease
                                            </TableCell>
                                            <TableCell
                                                align="left"
                                                className={clsx(
                                                    classes.tableRowLast,
                                                    classes.tableHasBorder,
                                                    classes.tableOutputContent,
                                                    plan === 'GO' &&
                                                        classes.tableOutputContentMed
                                                )}
                                            >
                                                {plan === 'GO' &&
                                                    `Call for pricing`}
                                                {plan === 'PRO' &&
                                                    `${formatter.format(
                                                        Math.round(
                                                            computeMonthLease(
                                                                proTotalHardwareCost,
                                                                Math.round(
                                                                    proTotalHardwareCost *
                                                                        0.2
                                                                )
                                                            )
                                                        )
                                                    )}`}
                                                {plan === 'Elite' &&
                                                    `${formatter.format(
                                                        Math.round(
                                                            computeMonthLease(
                                                                eliteTotalHardwareCost,
                                                                Math.round(
                                                                    eliteTotalHardwareCost *
                                                                        0.2
                                                                )
                                                            )
                                                        )
                                                    )}`}
                                                {plan === 'Unlimited' &&
                                                    `${formatter.format(
                                                        Math.round(
                                                            computeMonthLease(
                                                                unlimitedTotalHardwareCost,
                                                                Math.round(
                                                                    unlimitedTotalHardwareCost *
                                                                        0.2
                                                                )
                                                            )
                                                        )
                                                    )}`}
                                            </TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell
                                                component="th"
                                                scope="row"
                                                className={clsx(
                                                    classes.tableRowFirst,
                                                    classes.tableHasBorder,
                                                    classes.tableOutputTitle
                                                )}
                                            >
                                                Yearly subscription
                                            </TableCell>
                                            <TableCell
                                                align="left"
                                                className={clsx(
                                                    classes.tableRowLast,
                                                    classes.tableHasBorder,
                                                    classes.tableOutputContent
                                                )}
                                            >
                                                {plan === 'GO' && `$0`}
                                                {plan === 'PRO' &&
                                                    `${formatter.format(
                                                        proSoftwareFee
                                                    )}`}
                                                {plan === 'Elite' &&
                                                    `${formatter.format(
                                                        eliteSubscriptionCost
                                                    )}`}
                                                {plan === 'Unlimited' &&
                                                    `${formatter.format(
                                                        unlimitedSubscriptionCost
                                                    )}`}
                                            </TableCell>
                                        </TableRow>
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Grid>
                    </Grid>
                </section>
                <section className={classes.chartContainer}>
                    <Typography variant="h3" className={classes.chartMainTitle}>
                        AROYA {plan}
                    </Typography>
                    {plan === 'Unlimited' && (
                        <div className={classes.chartWrapper}>
                            <ResponsiveContainer>
                                <BarChart
                                    width={500}
                                    height={500}
                                    data={dataBar}
                                    margin={{}}
                                    className={classes.chart}
                                >
                                    <XAxis
                                        dataKey="name"
                                        axisLine={false}
                                        tickLine={false}
                                        tickSize={isDesktop ? 50 : 20}
                                    />
                                    <YAxis hide />
                                    <Tooltip
                                        formatter={(value, name, props) => [
                                            `${formatter.format(
                                                Math.round(value)
                                            )}`,
                                            name,
                                        ]}
                                        labelStyle={{
                                            fontWeight: 'bold',
                                            marginBottom: '5px',
                                        }}
                                    />
                                    <Legend
                                        align={isDesktop ? 'center' : 'left'}
                                    />
                                    <Bar
                                        dataKey="revenue"
                                        fill="#4EA093"
                                        name="Revenue Increase"
                                        label={{
                                            children: isDesktop
                                                ? 'Revenue Increase'
                                                : '',
                                            position: 'bottom',
                                        }}
                                    />
                                    <Bar
                                        dataKey="profit"
                                        fill="#3A7876"
                                        name="Profit Increase"
                                        label={{
                                            children: isDesktop
                                                ? 'Profit Increase'
                                                : '',
                                            position: 'bottom',
                                        }}
                                    />
                                    <Bar
                                        dataKey="swCost"
                                        stackId="a"
                                        fill="#122133"
                                        name="Software Cost"
                                        label={{
                                            children: isDesktop ? 'Cost' : '',
                                            position: 'bottom',
                                        }}
                                    />
                                    <Bar
                                        dataKey="hwDp"
                                        stackId="a"
                                        fill="#1D3C49"
                                        name="Hardware Down Payment"
                                    />
                                    <Bar
                                        dataKey="hwLease"
                                        stackId="a"
                                        fill="#2A585D"
                                        name="Hardware Lease"
                                    />
                                </BarChart>
                            </ResponsiveContainer>
                            {/* <div className={classes.chartText}>
                                <Typography
                                    variant="h3"
                                    className={classes.chartTitle}
                                >
                                    WHY NOW?
                                </Typography>
                                <Typography className={classes.chartContent}>
                                    Every month without <b>AROYA</b> is&nbsp;
                                    <b>
                                        {formatter.format(
                                            Math.round(unlimitedRevenueIncrease)
                                        )}
                                    </b>
                                    &nbsp;in lost profit
                                </Typography>
                            </div> */}
                        </div>
                    )}
                    {plan === 'Elite' && (
                        <div className={classes.chartWrapper}>
                            <ResponsiveContainer>
                                <BarChart
                                    width={500}
                                    height={500}
                                    data={dataBarElite}
                                    margin={{}}
                                    className={classes.chart}
                                >
                                    <XAxis
                                        dataKey="name"
                                        axisLine={false}
                                        tickLine={false}
                                        tickSize={isDesktop ? 50 : 20}
                                    />
                                    <YAxis hide />
                                    <Tooltip
                                        formatter={(value, name, props) => [
                                            `${formatter.format(
                                                Math.round(value)
                                            )}`,
                                            name,
                                        ]}
                                        labelStyle={{
                                            fontWeight: 'bold',
                                            marginBottom: '5px',
                                        }}
                                    />
                                    <Legend
                                        align={isDesktop ? 'center' : 'left'}
                                    />
                                    <Bar
                                        dataKey="revenue"
                                        fill="#4EA093"
                                        name="Revenue Increase"
                                        label={{
                                            children: isDesktop
                                                ? 'Revenue Increase'
                                                : '',
                                            position: 'bottom',
                                        }}
                                    />
                                    <Bar
                                        dataKey="profit"
                                        fill="#3A7876"
                                        name="Profit Increase"
                                        label={{
                                            children: isDesktop
                                                ? 'Profit Increase'
                                                : '',
                                            position: 'bottom',
                                        }}
                                    />
                                    <Bar
                                        dataKey="swCost"
                                        stackId="a"
                                        fill="#122133"
                                        name="Software Cost"
                                        label={{
                                            children: isDesktop ? 'Cost' : '',
                                            position: 'bottom',
                                        }}
                                    />
                                    <Bar
                                        dataKey="hwDp"
                                        stackId="a"
                                        fill="#1D3C49"
                                        name="Hardware Down Payment"
                                    />
                                    <Bar
                                        dataKey="hwLease"
                                        stackId="a"
                                        fill="#2A585D"
                                        name="Hardware Lease"
                                    />
                                </BarChart>
                            </ResponsiveContainer>
                            {/* <div className={classes.chartText}>
                                <Typography
                                    variant="h3"
                                    className={classes.chartTitle}
                                >
                                    WHY NOW?
                                </Typography>
                                <Typography className={classes.chartContent}>
                                    Every month without <b>AROYA</b> is&nbsp;
                                    <b>
                                        {formatter.format(
                                            Math.round(eliteRevenueIncrease)
                                        )}
                                    </b>
                                    &nbsp;in lost profit
                                </Typography>
                            </div> */}
                        </div>
                    )}
                    {plan === 'PRO' && (
                        <div className={classes.chartWrapper}>
                            <ResponsiveContainer>
                                <BarChart
                                    width={500}
                                    height={500}
                                    data={dataBarPro}
                                    margin={{}}
                                    className={classes.chart}
                                >
                                    <XAxis
                                        dataKey="name"
                                        axisLine={false}
                                        tickLine={false}
                                        tickSize={isDesktop ? 50 : 20}
                                    />
                                    <YAxis hide />
                                    <Tooltip
                                        formatter={(value, name, props) => [
                                            `${formatter.format(
                                                Math.round(value)
                                            )}`,
                                            name,
                                        ]}
                                        labelStyle={{
                                            fontWeight: 'bold',
                                            marginBottom: '5px',
                                        }}
                                    />
                                    <Legend
                                        align={isDesktop ? 'center' : 'left'}
                                    />
                                    <Bar
                                        dataKey="revenue"
                                        fill="#4EA093"
                                        name="Revenue Increase"
                                        label={{
                                            children: isDesktop
                                                ? 'Revenue Increase'
                                                : '',
                                            position: 'bottom',
                                        }}
                                    />
                                    <Bar
                                        dataKey="profit"
                                        fill="#3A7876"
                                        name="Profit Increase"
                                        label={{
                                            children: isDesktop
                                                ? 'Profit Increase'
                                                : '',
                                            position: 'bottom',
                                        }}
                                    />
                                    <Bar
                                        dataKey="swCost"
                                        stackId="a"
                                        fill="#122133"
                                        name="Software Cost"
                                        label={{
                                            children: isDesktop ? 'Cost' : '',
                                            position: 'bottom',
                                        }}
                                    />
                                    <Bar
                                        dataKey="hwDp"
                                        stackId="a"
                                        fill="#1D3C49"
                                        name="Hardware Down Payment"
                                    />
                                    <Bar
                                        dataKey="hwLease"
                                        stackId="a"
                                        fill="#2A585D"
                                        name="Hardware Lease"
                                    />
                                </BarChart>
                            </ResponsiveContainer>
                            {/* <div className={classes.chartText}>
                                <Typography
                                    variant="h3"
                                    className={classes.chartTitle}
                                >
                                    WHY NOW?
                                </Typography>
                                <Typography className={classes.chartContent}>
                                    Every month without <b>AROYA</b> is&nbsp;
                                    <b>
                                        {formatter.format(
                                            Math.round(proRevenueIncrease)
                                        )}
                                    </b>
                                    &nbsp;in lost profit
                                </Typography>
                            </div> */}
                        </div>
                    )}
                    {plan === 'GO' && (
                        <div className={classes.chartWrapper}>
                            <ResponsiveContainer>
                                <BarChart
                                    width={500}
                                    height={500}
                                    data={dataBarGo}
                                    margin={{}}
                                    className={classes.chart}
                                >
                                    <XAxis
                                        dataKey="name"
                                        axisLine={false}
                                        tickLine={false}
                                        tickSize={isDesktop ? 50 : 20}
                                    />
                                    <YAxis hide />
                                    <Tooltip
                                        formatter={(value, name, props) => [
                                            `${
                                                name ===
                                                    'Hardware Down Payment' ||
                                                name === 'Hardware Lease'
                                                    ? '*Call for pricing'
                                                    : formatter.format(
                                                          Math.round(value)
                                                      )
                                            }`,
                                            name,
                                        ]}
                                        labelStyle={{
                                            fontWeight: 'bold',
                                            marginBottom: '5px',
                                        }}
                                    />
                                    <Legend
                                        align={isDesktop ? 'center' : 'left'}
                                    />
                                    <Bar
                                        dataKey="revenue"
                                        fill="#4EA093"
                                        name="Revenue Increase"
                                        label={{
                                            children: isDesktop
                                                ? 'Revenue Increase'
                                                : '',
                                            position: 'bottom',
                                        }}
                                    />
                                    <Bar
                                        dataKey="profit"
                                        fill="#3A7876"
                                        name="Profit Increase"
                                        label={{
                                            children: isDesktop
                                                ? 'Profit Increase'
                                                : '',
                                            position: 'bottom',
                                        }}
                                    />
                                    <Bar
                                        dataKey="swCost"
                                        stackId="a"
                                        fill="#122133"
                                        name="Software Cost"
                                        label={{
                                            children: isDesktop ? 'Cost' : '',
                                            position: 'bottom',
                                        }}
                                    />
                                    <Bar
                                        dataKey="hwDp"
                                        stackId="a"
                                        fill="#1D3C49"
                                        name="Hardware Down Payment"
                                    />
                                    <Bar
                                        dataKey="hwLease"
                                        stackId="a"
                                        fill="#2A585D"
                                        name="Hardware Lease"
                                    />
                                </BarChart>
                            </ResponsiveContainer>
                            {/* <div className={classes.chartText}>
                                <Typography
                                    variant="h3"
                                    className={classes.chartTitle}
                                >
                                    WHY NOW?
                                </Typography>
                                <Typography className={classes.chartContent}>
                                    Every month without <b>AROYA</b> is&nbsp;
                                    <b>
                                        {formatter.format(
                                            Math.round(goRevenueIncrease)
                                        )}
                                    </b>
                                    &nbsp;in lost profit
                                </Typography>
                                <Typography className={classes.chartFootnote}>
                                    *For detailed pricing, please call&nbsp;
                                    <a href="tel:+1.509.332.2756">
                                        +1.509.332.2756
                                    </a>
                                </Typography>
                            </div> */}
                        </div>
                    )}
                </section>
                <section className={classes.featuresContainer}>
                    <Typography variant="h4" className={classes.featuresTitle}>
                        FEATURES
                    </Typography>
                    <TableContainer className={classes.tableContainer}>
                        <Table sx={{ minWidth: 650 }}>
                            <TableBody>
                                <TableRow>
                                    <TableCell
                                        component="th"
                                        scope="row"
                                        className={clsx(
                                            classes.tableRowFirst,
                                            classes.tableHasBorder,
                                            classes.tableFeatureTitle
                                        )}
                                    >
                                        Users
                                    </TableCell>
                                    <TableCell
                                        align="center"
                                        className={clsx(
                                            classes.tableRowLast,
                                            classes.tableHasBorder,
                                            classes.tableFeatureContent,
                                            plan === 'GO' && classes.tableNo
                                        )}
                                    >
                                        {plan === 'GO' && 'No'}
                                        {plan === 'PRO' && 'Unlimited'}
                                        {plan === 'Elite' && 'Unlimited'}
                                        {plan === 'Unlimited' && 'Unlimited'}
                                    </TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell
                                        component="th"
                                        scope="row"
                                        className={clsx(
                                            classes.tableRowFirst,
                                            classes.tableHasBorder,
                                            classes.tableFeatureTitle
                                        )}
                                    >
                                        METRC Integration
                                    </TableCell>
                                    <TableCell
                                        align="center"
                                        className={clsx(
                                            classes.tableRowLast,
                                            classes.tableHasBorder,
                                            classes.tableFeatureContent,
                                            plan === 'GO' && classes.tableNo,
                                            plan === 'PRO' && classes.tableNo
                                        )}
                                    >
                                        {plan === 'GO' && 'No'}
                                        {plan === 'PRO' && 'No'}
                                        {plan === 'Elite' && 'Yes'}
                                        {plan === 'Unlimited' && 'Yes'}
                                    </TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell
                                        component="th"
                                        scope="row"
                                        className={clsx(
                                            classes.tableRowFirst,
                                            classes.tableHasBorder,
                                            classes.tableFeatureTitle
                                        )}
                                    >
                                        Analytics
                                    </TableCell>
                                    <TableCell
                                        align="center"
                                        className={clsx(
                                            classes.tableRowLast,
                                            classes.tableHasBorder,
                                            classes.tableFeatureContent,
                                            plan === 'GO' && classes.tableNo,
                                            plan === 'PRO' && classes.tableNo
                                        )}
                                    >
                                        {plan === 'GO' && 'No'}
                                        {plan === 'PRO' && 'No'}
                                        {plan === 'Elite' && 'Yes'}
                                        {plan === 'Unlimited' && 'Yes'}
                                    </TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell
                                        component="th"
                                        scope="row"
                                        className={clsx(
                                            classes.tableRowFirst,
                                            classes.tableHasBorder,
                                            classes.tableFeatureTitle
                                        )}
                                    >
                                        Customer success calls
                                    </TableCell>
                                    <TableCell
                                        align="center"
                                        className={clsx(
                                            classes.tableRowLast,
                                            classes.tableHasBorder,
                                            classes.tableFeatureContent,
                                            plan === 'GO' && classes.tableNo,
                                            plan === 'PRO' && classes.tableNo
                                        )}
                                    >
                                        {plan === 'GO' && 'No'}
                                        {plan === 'PRO' && 'No'}
                                        {plan === 'Elite' && 'Monthly'}
                                        {plan === 'Unlimited' && 'Weekly'}
                                    </TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell
                                        component="th"
                                        scope="row"
                                        className={clsx(
                                            classes.tableRowFirst,
                                            classes.tableHasBorder,
                                            classes.tableFeatureTitle
                                        )}
                                    >
                                        Guaranteed Yield Increase*
                                    </TableCell>
                                    <TableCell
                                        align="center"
                                        className={clsx(
                                            classes.tableRowLast,
                                            classes.tableHasBorder,
                                            classes.tableFeatureContent,
                                            plan === 'GO' && classes.tableNo,
                                            plan === 'PRO' && classes.tableNo,
                                            plan === 'Elite' && classes.tableNo
                                        )}
                                    >
                                        {plan === 'GO' && 'No'}
                                        {plan === 'PRO' && 'No'}
                                        {plan === 'Elite' && 'No'}
                                        {plan === 'Unlimited' && '10%'}
                                    </TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell
                                        component="th"
                                        scope="row"
                                        className={clsx(
                                            classes.tableRowFirst,
                                            classes.tableHasBorder,
                                            classes.tableFeatureTitle
                                        )}
                                    >
                                        Cost per sensor
                                    </TableCell>
                                    <TableCell
                                        align="center"
                                        className={clsx(
                                            classes.tableRowLast,
                                            classes.tableHasBorder,
                                            classes.tableFeatureContent
                                        )}
                                    >
                                        {plan === 'GO' && (
                                            <div className={classes.tableHasBr}>
                                                <span>Substrate</span> -
                                                <br />
                                                <span>Climate</span> -
                                                <br />
                                                <span>Repeater</span> -
                                                <br />
                                                <span>Gateway</span> -
                                            </div>
                                        )}
                                        {plan === 'PRO' && (
                                            <div className={classes.tableHasBr}>
                                                <span>Substrate</span> $538
                                                <div
                                                    className={
                                                        classes.dataTransmission
                                                    }
                                                >
                                                    Data Transmission Included
                                                </div>
                                                <span>Climate</span> $538
                                                <div
                                                    className={
                                                        classes.dataTransmission
                                                    }
                                                >
                                                    Data Transmission Included
                                                </div>
                                                <span>Repeater</span> $305
                                                <br />
                                                <span>Gateway</span> $538
                                            </div>
                                        )}
                                        {plan === 'Elite' && (
                                            <div className={classes.tableHasBr}>
                                                <span>Substrate</span> $414
                                                <div
                                                    className={
                                                        classes.dataTransmission
                                                    }
                                                >
                                                    Data Transmission Included
                                                </div>
                                                <span>Climate</span> $414
                                                <div
                                                    className={
                                                        classes.dataTransmission
                                                    }
                                                >
                                                    Data Transmission Included
                                                </div>
                                                <span>Repeater</span> $254
                                                <br />
                                                <span>Gateway</span> $414
                                            </div>
                                        )}
                                        {plan === 'Unlimited' && (
                                            <div className={classes.tableHasBr}>
                                                <span>Substrate</span> $414
                                                <div
                                                    className={
                                                        classes.dataTransmission
                                                    }
                                                >
                                                    Data Transmission Included
                                                </div>
                                                <span>Climate</span> $414
                                                <div
                                                    className={
                                                        classes.dataTransmission
                                                    }
                                                >
                                                    Data Transmission Included
                                                </div>
                                                <span>Repeater</span> $254
                                                <br />
                                                <span>Gateway</span> $414
                                            </div>
                                        )}
                                    </TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </TableContainer>
                </section>
            </main>
            <footer className={classes.footer}>
                <div className={classes.footerLeft}>
                    <Link
                        to="/aroya-plans?unlock=true"
                        className={classes.editLink}
                    >
                        <Button
                            variant="outlined"
                            color="secondary"
                            disableElevation
                            className={classes.editButton}
                        >
                            Edit
                        </Button>
                    </Link>
                </div>
                {/* <div className={classes.footerRight}>
                    <div className={classes.footerImage}>
                        <svg
                            width="150"
                            height="75"
                            viewBox="0 0 317 134"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                d="M249.907 125.681V91.6885L257.779 93.3034V115.53V129.639C257.925 130.447 253.493 132.169 234.601 132.6C215.709 133.031 213.61 125.961 214.922 122.372C219.47 112.683 235.476 118.335 242.91 122.372L249.907 125.681Z"
                                fill="#4DA556"
                            />
                            <path
                                fillRule="evenodd"
                                clipRule="evenodd"
                                d="M249.076 90.6697L258.61 92.6256V129.589C258.653 130.075 258.354 130.426 258.145 130.608C257.901 130.82 257.57 130.996 257.19 131.152C256.416 131.467 255.216 131.784 253.468 132.08C249.959 132.676 244.082 133.215 234.62 133.431C225.107 133.648 219.619 131.988 216.668 129.644C213.649 127.246 213.369 124.197 214.141 122.087L214.154 122.052L214.169 122.019C216.685 116.659 222.344 115.682 227.939 116.4C233.565 117.123 239.515 119.585 243.286 121.631L249.076 124.369V90.6697ZM250.738 92.7075V126.993L242.534 123.114L242.513 123.103C238.849 121.113 233.09 118.738 227.727 118.049C222.338 117.357 217.73 118.39 215.69 122.691C215.164 124.17 215.294 126.431 217.702 128.343C220.195 130.323 225.202 131.983 234.582 131.769C244.012 131.554 249.797 131.017 253.19 130.442C254.893 130.153 255.954 129.861 256.561 129.613C256.745 129.538 256.868 129.474 256.948 129.427V93.9814L250.738 92.7075ZM257.067 129.342C257.067 129.342 257.066 129.343 257.063 129.346C257.065 129.343 257.067 129.342 257.067 129.342Z"
                                fill="#4DA556"
                            />
                            <path
                                d="M271.684 125.681V91.6885L263.813 93.3034V115.53V129.639C263.667 130.447 268.098 132.169 286.99 132.6C305.883 133.031 307.982 125.961 306.67 122.372C302.122 112.683 286.116 118.335 278.681 122.372L271.684 125.681Z"
                                fill="#4DA556"
                            />
                            <path
                                fillRule="evenodd"
                                clipRule="evenodd"
                                d="M272.516 90.6697L262.982 92.6256V129.589C262.938 130.075 263.237 130.426 263.447 130.608C263.69 130.82 264.021 130.996 264.402 131.152C265.175 131.467 266.375 131.784 268.123 132.08C271.632 132.676 277.509 133.215 286.972 133.431C296.484 133.648 301.973 131.988 304.924 129.644C307.943 127.246 308.222 124.197 307.45 122.087L307.438 122.052L307.422 122.019C304.906 116.659 299.247 115.682 293.652 116.4C288.026 117.123 282.077 119.585 278.305 121.631L272.516 124.369V90.6697ZM270.853 92.7075V126.993L279.058 123.114L279.078 123.103C282.742 121.113 288.501 118.738 293.864 118.049C299.254 117.357 303.861 118.39 305.902 122.691C306.428 124.17 306.297 126.431 303.89 128.343C301.396 130.323 296.389 131.983 287.01 131.769C277.58 131.554 271.795 131.017 268.401 130.442C266.698 130.153 265.637 129.861 265.03 129.613C264.847 129.538 264.723 129.474 264.644 129.427V93.9814L270.853 92.7075ZM264.525 129.342C264.525 129.342 264.526 129.343 264.529 129.346C264.526 129.343 264.525 129.342 264.525 129.342Z"
                                fill="#4DA556"
                            />
                            <path
                                fillRule="evenodd"
                                clipRule="evenodd"
                                d="M258.791 103.752L260.19 101.569L261.449 103.807V103.959C261.532 104.057 261.615 104.155 261.699 104.252L261.841 104.505L261.847 104.422C270.055 113.737 283.275 115.58 289.797 115.33C290.191 115.315 290.375 114.848 290.123 114.544C286.202 109.81 284.847 104.226 284.663 101.927C303.154 103.819 312.963 96.8481 316.307 92.2094C316.874 91.4218 316.732 90.3554 316.032 89.6825C306.452 80.4755 289.096 81.4854 281.471 83.0663C298.027 70.101 304.77 49.4944 306.708 37.0782C307.103 34.5482 304.627 32.7281 302.353 33.9039C289.109 40.749 277.007 54.3836 272.185 61.0132C274.493 40.705 266.095 12.979 261.529 1.44051L261.524 1.36945C260.963 0.247039 259.37 0.237287 258.791 1.34019V1.23768C254.245 12.6511 245.733 40.585 248.055 61.0132C243.388 54.5967 231.902 41.6186 219.161 34.5848C216.322 33.0171 213.203 35.3093 213.769 38.5031C215.988 51.0233 222.832 70.5853 238.769 83.0663C231.391 81.5366 215.139 80.844 205.433 89.1655C204.106 90.3031 203.86 92.247 205.009 93.5639C208.921 98.0483 218.411 103.683 235.577 101.927C235.403 104.104 234.179 109.229 230.717 113.787C230.24 114.415 230.626 115.347 231.415 115.351C238.324 115.39 251 113.223 258.791 103.959V103.752Z"
                                fill="#4DA556"
                            />
                            <path
                                d="M255.355 78.6681C255.355 80.7759 253.646 82.4846 251.538 82.4846C249.431 82.4846 247.722 80.7759 247.722 78.6681C247.722 76.5603 249.431 74.8516 251.538 74.8516C253.646 74.8516 255.355 76.5603 255.355 78.6681Z"
                                fill="#E5E5E5"
                            />
                            <path
                                d="M272.042 78.6681C272.042 80.7759 270.334 82.4846 268.226 82.4846C266.118 82.4846 264.409 80.7759 264.409 78.6681C264.409 76.5603 266.118 74.8516 268.226 74.8516C270.334 74.8516 272.042 76.5603 272.042 78.6681Z"
                                fill="#E5E5E5"
                            />
                            <path
                                fillRule="evenodd"
                                clipRule="evenodd"
                                d="M243.479 83.9207C243.795 83.588 244.321 83.5746 244.654 83.8908C247.417 86.5166 252.368 89.6769 258.181 90.5953C263.945 91.506 270.604 90.2243 276.922 83.9055C277.247 83.581 277.773 83.581 278.098 83.9055C278.422 84.2301 278.422 84.7563 278.098 85.0809C271.385 91.7936 264.185 93.2268 257.921 92.2371C251.706 91.2551 246.459 87.8996 243.509 85.0956C243.176 84.7794 243.163 84.2534 243.479 83.9207Z"
                                fill="#E5E5E5"
                            />
                            <rect
                                x="0.786316"
                                y="10.5974"
                                width="181.264"
                                height="38.2539"
                                rx="19.127"
                                stroke="black"
                            />
                            <path
                                d="M36.2689 39.2244V30.8994C36.2689 27.9744 34.7689 26.2494 31.8939 26.2494C30.4689 26.2494 28.7189 27.0244 28.0689 28.4744L27.8939 26.5744H26.0439V39.2244H28.1689V33.3494C28.1689 32.3744 28.1939 31.0494 28.6189 30.1244C29.1689 28.9744 30.1439 28.1744 31.3689 28.1744C33.3689 28.1744 34.1439 29.2994 34.1439 31.1994V39.2244H36.2689ZM42.993 23.6744V21.1744H40.518V23.6744H42.993ZM42.843 39.2244V26.5744H40.668V39.2244H42.843ZM57.3289 34.9744H55.1789C55.0789 35.7994 54.7789 36.3744 54.3289 36.8494C53.8289 37.3744 53.0539 37.6994 52.1789 37.6994C51.2039 37.6994 50.5289 37.3494 49.9789 36.8744C48.9789 36.0244 48.7039 34.4494 48.7039 32.8994C48.7039 31.3494 48.9789 29.7744 49.9789 28.9244C50.5289 28.4494 51.2039 28.0994 52.1789 28.0994C53.0039 28.0994 53.7289 28.3994 54.2289 28.8744C54.7039 29.3494 55.0289 29.9744 55.1289 30.8494H57.2789C57.1789 29.4744 56.6289 28.4244 55.7789 27.5994C54.9039 26.7494 53.6789 26.2494 52.1789 26.2494C49.9039 26.2494 48.4789 27.2994 47.5789 28.6244C46.9039 29.6244 46.5289 30.9994 46.5289 32.8994C46.5289 34.7994 46.9039 36.1744 47.5789 37.1744C48.4789 38.4994 49.9289 39.5494 52.2039 39.5494C53.7539 39.5494 54.9539 39.0494 55.8539 38.1744C56.7039 37.3494 57.2289 36.3244 57.3289 34.9744ZM71.0096 32.3244C71.0346 30.6244 70.4346 28.9994 69.4096 27.8744C68.4846 26.8744 67.1846 26.2494 65.5596 26.2494C63.3596 26.2494 61.6846 27.3744 60.7846 28.8994C60.0846 30.0744 59.8096 31.4244 59.8096 32.9494C59.8096 34.7494 60.3346 36.4244 61.3846 37.6494C62.3596 38.7994 63.8096 39.5494 65.5846 39.5494C66.7846 39.5494 67.9096 39.1994 68.8096 38.5994C69.8346 37.8994 70.5846 36.7244 70.7596 35.4744H68.6846C68.5096 36.1494 68.2846 36.4994 67.9346 36.8494C67.4096 37.3994 66.5846 37.6744 65.5846 37.6744C64.6096 37.6744 63.8346 37.3494 63.2596 36.7744C62.4846 35.9994 62.0346 34.7494 62.0346 33.5994H70.9846L71.0096 32.3244ZM68.8346 31.7744H62.0846C62.1346 29.9244 63.2596 28.0494 65.5596 28.0494C66.6846 28.0494 67.5346 28.4994 68.0596 29.1994C68.5596 29.8744 68.8346 30.8244 68.8346 31.7744ZM82.9158 23.6744V21.1744H80.4408V23.6744H82.9158ZM82.7658 40.9994V26.5744H80.6408V41.0244C80.6408 41.8744 80.1658 42.6244 79.2158 42.6244C78.6908 42.6244 78.1658 42.5994 77.6408 42.4994V44.3244C78.2658 44.5244 78.6408 44.5744 79.2908 44.5744C81.6908 44.5744 82.7658 43.1994 82.7658 40.9994ZM97.6959 32.8994C97.6959 30.8994 97.2709 29.0744 95.8959 27.7244C94.9709 26.8244 93.6709 26.2494 92.0709 26.2494C90.4709 26.2494 89.1709 26.8244 88.2459 27.7244C86.8709 29.0744 86.4459 30.8994 86.4459 32.8994C86.4459 34.8994 86.8709 36.7244 88.2459 38.0744C89.1709 38.9744 90.4709 39.5494 92.0709 39.5494C93.6709 39.5494 94.9709 38.9744 95.8959 38.0744C97.2709 36.7244 97.6959 34.8994 97.6959 32.8994ZM95.5209 32.8994C95.5209 34.3744 95.2459 35.8994 94.3459 36.8244C93.8209 37.3494 93.0709 37.7244 92.0709 37.7244C91.0709 37.7244 90.3209 37.3494 89.7959 36.8244C88.8959 35.8994 88.6209 34.3744 88.6209 32.8994C88.6209 31.4244 88.8959 29.8994 89.7959 28.9744C90.3209 28.4494 91.0709 28.0744 92.0709 28.0744C93.0709 28.0744 93.8209 28.4494 94.3459 28.9744C95.2459 29.8994 95.5209 31.4244 95.5209 32.8994ZM112.561 32.9244C112.561 30.7494 111.911 28.7494 110.636 27.5244C109.811 26.7494 108.636 26.2494 107.161 26.2494C105.786 26.2494 104.161 26.8994 103.486 28.0494V21.1744H101.361V39.2244H103.236L103.386 37.5744C104.111 38.8494 105.736 39.5494 107.161 39.5494C108.711 39.5494 109.911 39.0244 110.736 38.1744C111.936 36.9494 112.561 35.0244 112.561 32.9244ZM110.386 32.8994C110.386 35.2244 109.636 37.7494 106.811 37.7494C105.886 37.7494 105.261 37.3744 104.761 36.9244C103.711 35.9994 103.361 34.4744 103.361 32.8994C103.361 31.3244 103.711 29.7994 104.761 28.8744C105.261 28.4244 105.886 28.0494 106.811 28.0494C109.636 28.0494 110.386 30.5744 110.386 32.8994ZM133.191 32.9244C133.191 30.7494 132.541 28.7494 131.266 27.5244C130.441 26.7494 129.266 26.2494 127.791 26.2494C126.416 26.2494 124.791 26.8994 124.116 28.0494V21.1744H121.991V39.2244H123.866L124.016 37.5744C124.741 38.8494 126.366 39.5494 127.791 39.5494C129.341 39.5494 130.541 39.0244 131.366 38.1744C132.566 36.9494 133.191 35.0244 133.191 32.9244ZM131.016 32.8994C131.016 35.2244 130.266 37.7494 127.441 37.7494C126.516 37.7494 125.891 37.3744 125.391 36.9244C124.341 35.9994 123.991 34.4744 123.991 32.8994C123.991 31.3244 124.341 29.7994 125.391 28.8744C125.891 28.4244 126.516 28.0494 127.441 28.0494C130.266 28.0494 131.016 30.5744 131.016 32.8994ZM146.759 39.2244V26.5744H144.634V33.1494C144.634 34.4494 144.384 35.8244 143.584 36.6494C143.034 37.2244 142.334 37.5994 141.484 37.5994C139.484 37.5994 138.784 36.4744 138.784 34.5744V26.5744H136.659V34.8744C136.659 37.7994 138.109 39.5244 140.984 39.5244C142.459 39.5244 144.084 38.7494 144.734 37.2994L144.909 39.2244H146.759ZM161.635 39.2244V21.1744H159.51V28.0494C158.835 26.8994 157.21 26.2494 155.835 26.2494C154.36 26.2494 153.185 26.7494 152.36 27.5244C151.085 28.7494 150.435 30.7494 150.435 32.9244C150.435 35.0244 151.06 36.9494 152.26 38.1744C153.085 39.0244 154.285 39.5494 155.835 39.5494C157.26 39.5494 158.885 38.8494 159.61 37.5744L159.76 39.2244H161.635ZM159.635 32.8994C159.635 34.4744 159.285 35.9994 158.235 36.9244C157.735 37.3744 157.11 37.7494 156.185 37.7494C153.36 37.7494 152.61 35.2244 152.61 32.8994C152.61 30.5744 153.36 28.0494 156.185 28.0494C157.11 28.0494 157.735 28.4244 158.235 28.8744C159.285 29.7994 159.635 31.3244 159.635 32.8994Z"
                                fill="black"
                            />
                        </svg>
                    </div>
                    <div className={classes.footerCta}>
                        <Typography
                            variant="h6"
                            className={classes.footerTitle}
                        >
                            LIKE WHAT YOU SEE?
                        </Typography>
                        <Typography
                            variant="body2"
                            className={classes.footerContent}
                        >
                            Share with your team or book a demo today.
                        </Typography>
                        <div className={classes.footerEmail}>
                            <TextField
                                className={classes.emailInput}
                                variant="outlined"
                                color="secondary"
                            />
                            <Button
                                variant="contained"
                                color="secondary"
                                className={classes.emailButton}
                            >
                                Email Report
                            </Button>
                        </div>
                    </div>
                </div> */}
            </footer>
        </div>
    );
};

export default YourPlan;
